import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Flex, Text, Button, Box, IconButton, Separator, Tabs, Select, Tooltip,
  TextField, TextArea, Badge, Dialog, AlertDialog,
} from "@radix-ui/themes";
import {
  ArrowLeftIcon, TrashIcon, CopyIcon, PlusIcon, ChevronUpIcon, ChevronDownIcon,
  DragHandleDots2Icon, ImageIcon, TextIcon, CodeIcon, DividerHorizontalIcon,
  Link2Icon, StarIcon, ColumnsIcon, WidthIcon,
  EnvelopeOpenIcon,
} from "@radix-ui/react-icons";
import { ComputerDesktopIcon, DevicePhoneMobileIcon } from "@heroicons/react/16/solid";
import { useProfileContext } from "context";
import { useMarketingEmailTemplates, useMarketingSenderDomains } from "hooks";
import { useSurveyTemplates } from "hooks/bSurveys/useSurveyTemplates";
import { ToastAlert } from "components";
import { sendTestEmail } from "../../api/bMarketingApi";
import { useToast } from "../../context/ToastContext";
import {
  getSocialIconEmailSrc,
  prepareScratchSocialIconsForEmail,
  SOCIAL_ICON_DEFINITIONS,
} from "./scratchSocialIcons";

/* ─────────────────────────── helpers ─────────────────────────── */
let _idCounter = 0;
const uid = () => `blk_${++_idCounter}_${Date.now()}`;
const cloneScratchValue = (value) => JSON.parse(JSON.stringify(value));
const createDesignerSnapshot = (name, subject, blocksArr, design) => ({
  name,
  subject,
  blocks: cloneScratchValue(blocksArr),
  design: cloneScratchValue(design),
});
const FOOTER_LINE_DEFAULTS = {
  custom: { text: "" },
  browser: {
    linkText: "view email in browser",
  },
  preferences: {
    introText: "",
    preferencesText: "update your preferences",
    separatorText: " or ",
    unsubscribeText: "unsubscribe",
  },
  copyright: { text: "© {year} {brandName}. All rights reserved." },
};
const createFooterLine = (type, overrides = {}) => ({
  id: overrides.id || `footer_${type}_${uid()}`,
  type,
  ...(FOOTER_LINE_DEFAULTS[type] || FOOTER_LINE_DEFAULTS.custom),
  ...overrides,
});
const getDefaultFooterLines = () => [
  createFooterLine("browser"),
  createFooterLine("preferences"),
  createFooterLine("copyright"),
];
const getImageSizeNumber = (block) => {
  const imageSize = parseInt(block?.imageSize, 10);
  if (Number.isFinite(imageSize) && imageSize > 0) return imageSize;
  const legacyWidth = parseInt(block?.width, 10);
  if (Number.isFinite(legacyWidth) && legacyWidth > 0 && String(block?.width || "").includes("px")) return legacyWidth;
  return 320;
};
const getImageWidthValue = (block) => {
  const imageSize = parseInt(block?.imageSize, 10);
  if (Number.isFinite(imageSize) && imageSize > 0) return `${imageSize}px`;
  if (block?.width) return block.width;
  return "100%";
};
const ABSOLUTE_URL_PATTERN = /^(https?:\/\/|mailto:|tel:|#)/i;

const ensureAbsoluteUrl = (url) => {
  const trimmed = (url || "").trim();
  if (!trimmed) return "";
  if (ABSOLUTE_URL_PATTERN.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const getPublicSurveyBaseUrl = () => {
  const configuredBaseUrl = process.env.REACT_APP_BSURVEY_PUBLIC_URL;
  if (configuredBaseUrl && configuredBaseUrl.trim()) {
    return configuredBaseUrl.trim().replace(/\/+$/, "");
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, "");
  }
  return "";
};

const getPublicSurveyLink = (templateId) => {
  if (!templateId) return "";
  const baseUrl = getPublicSurveyBaseUrl();
  return baseUrl ? `${baseUrl}/surveys/${templateId}` : "";
};

const addSurveyRecipientPlaceholder = (url) => {
  if (!url) return "";
  if (url.includes("email=")) return url;
  return `${url}${url.includes("?") ? "&" : "?"}email={{marketing_recipient_email}}`;
};

const getPublicSurveyEmailLink = (templateId) => addSurveyRecipientPlaceholder(getPublicSurveyLink(templateId));

const getReviewSurveyUrl = (block) => (
  getPublicSurveyEmailLink(block?.surveyTemplateId) || addSurveyRecipientPlaceholder(block?.surveyUrl || "")
);

const getMediaLinkUrl = (block) => {
  const linkUrl = block?.linkUrl?.trim();
  return linkUrl ? ensureAbsoluteUrl(linkUrl) : "";
};
const getMediaHrefValue = (block) => getMediaLinkUrl(block) || "#";
const DEFAULT_EMAIL_DESIGN = {
  bgColor: "#F3F4F6",
  contentBgColor: "#FFFFFF",
  contentWidth: "600",
  contentBorderRadius: "10",
  contentPadding: "0",
  globalFontFamily: "Arial, sans-serif",
  footerBgColor: "#F9FAFB",
  footerTextColor: "#9CA3AF",
  footerLinkColor: "#9CA3AF",
  footerFontSize: "12",
  footerLines: getDefaultFooterLines(),
};
const MAX_SCRATCH_IMAGE_SIZE_BYTES = 1048576;
const SCRATCH_ASSET_FIELD_PREFIX = "scratch_asset__";
const SCRATCH_ASSET_PLACEHOLDER_PREFIX = "scratch-upload://";
const REVIEW_STAR_COUNT = 5;
const DEFAULT_REVIEW_STAR_SIZE = 32;
const DEFAULT_REVIEW_CLICK_THROUGH_MIN_RATING = 4;
const REVIEW_CLICK_THROUGH_MIN_RATING_OPTIONS = [
  { value: "4", label: "4 and 5 stars" },
  { value: "5", label: "5 stars only" },
];
const NO_SURVEY_TEMPLATE_VALUE = "__no_survey_template__";
const REVIEW_COMPACT_STAR_SCALE = 0.625;
const REVIEW_CARD_SHADOW = "0 10px 24px rgba(17, 24, 39, 0.06)";
const FOOTER_SPLIT_LINE_GAP = "2px 0 0";
const createScratchAssetKey = () => `asset_${uid()}`;
const buildScratchAssetFieldName = (assetKey) => `${SCRATCH_ASSET_FIELD_PREFIX}${assetKey}`;
const buildScratchAssetPlaceholder = (assetKey) => `${SCRATCH_ASSET_PLACEHOLDER_PREFIX}${assetKey}`;
const isImageLikeBlockType = (type) => type === "image" || type === "logo";
const getFlexAlignmentForText = (align) => {
  if (align === "left") return "flex-start";
  if (align === "right") return "flex-end";
  return "center";
};
const getLogoSizeNumber = (block) => {
  const logoWidth = parseInt(block?.logoWidth, 10);
  if (Number.isFinite(logoWidth) && logoWidth > 0) return logoWidth;
  return 150;
};
const getReviewStarSize = (block, compact = false) => {
  const configuredSize = parseInt(block?.starSize, 10);
  const baseSize = Number.isFinite(configuredSize) && configuredSize > 0
    ? configuredSize
    : DEFAULT_REVIEW_STAR_SIZE;
  if (!compact) return baseSize;
  return Math.max(14, Math.round(baseSize * REVIEW_COMPACT_STAR_SCALE));
};
const getReviewClickThroughMinRating = (block) => {
  const configuredRating = parseInt(block?.clickThroughMinRating, 10);
  if (configuredRating === 5) return 5;
  return DEFAULT_REVIEW_CLICK_THROUGH_MIN_RATING;
};
const getReviewCardShadow = (compact = false) => (compact ? "none" : REVIEW_CARD_SHADOW);
const normalizeFooterLine = (line) => {
  const type = line?.type && FOOTER_LINE_DEFAULTS[line.type] ? line.type : "custom";
  return {
    id: line?.id || `footer_${type}_${uid()}`,
    type,
    ...(FOOTER_LINE_DEFAULTS[type] || FOOTER_LINE_DEFAULTS.custom),
    ...line,
  };
};
const getFooterLines = (design) => {
  const sourceLines = Array.isArray(design?.footerLines) && design.footerLines.length
    ? design.footerLines
    : getDefaultFooterLines();
  const seenRequiredTypes = new Set();

  const normalizedLines = sourceLines
    .map(normalizeFooterLine)
    .filter((line) => {
      if (line.type === "custom") return true;
      if (seenRequiredTypes.has(line.type)) return false;
      seenRequiredTypes.add(line.type);
      return true;
    });

  if (!seenRequiredTypes.has("browser")) normalizedLines.unshift(createFooterLine("browser"));
  if (!seenRequiredTypes.has("preferences")) normalizedLines.push(createFooterLine("preferences"));
  if (!seenRequiredTypes.has("copyright")) normalizedLines.push(createFooterLine("copyright"));

  return normalizedLines;
};
const normalizeEmailDesign = (design = {}) => {
  const merged = { ...DEFAULT_EMAIL_DESIGN, ...design };
  return {
    ...merged,
    footerLines: getFooterLines(merged),
  };
};
const getFooterFontSize = (design) => {
  const fontSize = parseInt(design?.footerFontSize, 10);
  return Number.isFinite(fontSize) && fontSize >= 10 && fontSize <= 18 ? fontSize : 12;
};
const resolveFooterTemplateText = (value, brandName) => String(value || "")
  .replace(/\{year\}/g, String(new Date().getFullYear()))
  .replace(/\{brandName\}/g, brandName || "Your Brand");
const getReviewBlocksMissingLinks = (blocksArr = []) => {
  const invalidBlocks = [];
  const visitBlock = (block) => {
    if (!block) return;
    if (block.type === "review") {
      const hasReviewUrl = Boolean(block.url?.trim());
      const hasSurveyUrl = Boolean(getReviewSurveyUrl(block).trim());
      if (!hasReviewUrl || !hasSurveyUrl) {
        invalidBlocks.push(block);
      }
    }
    if (block.type === "columns") {
      (block.columns || []).forEach((column) => {
        (column.blocks || []).forEach(visitBlock);
      });
    }
  };

  (blocksArr || []).forEach(visitBlock);
  return invalidBlocks;
};
const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
  reader.readAsDataURL(file);
});

/* ─────────────────────────── block definitions ─────────────────────────── */
const CONTENT_BLOCKS = [
  { type: "image",     label: "Image",     description: "Upload or link an image" },
  { type: "heading",   label: "Heading",   description: "Title or headline text" },
  { type: "paragraph", label: "Paragraph", description: "Body text content" },
  { type: "button",    label: "Button",    description: "Call-to-action button" },
  { type: "review",    label: "Review",    description: "Clickable review prompt" },
  { type: "divider",   label: "Divider",   description: "Horizontal separator" },
  { type: "spacer",    label: "Spacer",    description: "Empty vertical space" },
  { type: "social",    label: "Social",    description: "Social media links" },
  { type: "logo",      label: "Logo",      description: "Brand logo image" },
];

const LAYOUT_BLOCKS = [
  { colCount: 2, label: "2 Columns" },
  { colCount: 3, label: "3 Columns" },
  { colCount: 4, label: "4 Columns" },
];

const COLUMN_LAYOUT_PRESETS = {
  2: [
    { id: "equal", label: "Equal", widths: [50, 50] },
    { id: "33-67", label: "33% | 67%", widths: [33, 67] },
    { id: "67-33", label: "67% | 33%", widths: [67, 33] },
    { id: "25-75", label: "25% | 75%", widths: [25, 75] },
    { id: "75-25", label: "75% | 25%", widths: [75, 25] },
  ],
  3: [
    { id: "equal", label: "Equal", widths: [34, 33, 33] },
    { id: "50-25-25", label: "50% | 25% | 25%", widths: [50, 25, 25] },
    { id: "25-50-25", label: "25% | 50% | 25%", widths: [25, 50, 25] },
    { id: "25-25-50", label: "25% | 25% | 50%", widths: [25, 25, 50] },
    { id: "20-60-20", label: "20% | 60% | 20%", widths: [20, 60, 20] },
  ],
  4: [
    { id: "equal", label: "Equal", widths: [25, 25, 25, 25] },
    { id: "40-20-20-20", label: "40% | 20% | 20% | 20%", widths: [40, 20, 20, 20] },
    { id: "20-40-20-20", label: "20% | 40% | 20% | 20%", widths: [20, 40, 20, 20] },
    { id: "20-20-40-20", label: "20% | 20% | 40% | 20%", widths: [20, 20, 40, 20] },
    { id: "20-20-20-40", label: "20% | 20% | 20% | 40%", widths: [20, 20, 20, 40] },
  ],
};

const getColumnLayoutPresets = (colCount) => COLUMN_LAYOUT_PRESETS[colCount] || [
  {
    id: "equal",
    label: "Equal",
    widths: Array.from({ length: Math.max(colCount || 1, 1) }, () => Math.round(100 / Math.max(colCount || 1, 1))),
  },
];

const getDefaultColumnWidths = (colCount) => {
  const defaultPreset = getColumnLayoutPresets(colCount)[0];
  return [...(defaultPreset?.widths || [100])];
};

const getNormalizedColumnWidths = (block) => {
  const columnCount = Math.max(block?.columns?.length || 1, 1);
  const fallbackWidths = getDefaultColumnWidths(columnCount);
  const rawWidths = Array.isArray(block?.columnWidths) ? block.columnWidths : [];
  if (rawWidths.length !== columnCount) return fallbackWidths;

  const numericWidths = rawWidths.map((value) => Number(value));
  if (numericWidths.some((value) => !Number.isFinite(value) || value <= 0)) return fallbackWidths;

  const totalWidth = numericWidths.reduce((sum, value) => sum + value, 0);
  if (totalWidth <= 0) return fallbackWidths;

  return numericWidths.map((value) => Math.round((value / totalWidth) * 10000) / 100);
};

const formatColumnWidthPercent = (value) => `${Number(value.toFixed(2)).toString().replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1")}%`;

const widthsMatchPreset = (a, b) => a.length === b.length && a.every((value, index) => Math.abs(value - b[index]) < 0.05);

const getSelectedColumnLayoutId = (block) => {
  const widths = getNormalizedColumnWidths(block);
  const presets = getColumnLayoutPresets(block?.columns?.length || widths.length);
  return presets.find((preset) => widthsMatchPreset(widths, preset.widths))?.id || null;
};

const defaultBlock = (type) => {
  const base = { id: uid(), type, sectionBg: "transparent", sectionPadding: "0" };
  switch (type) {
    case "image":
      return { ...base, src: "", alt: "Image", linkUrl: "", imageSize: "320", align: "center", borderRadius: "0", fullBleed: false };
    case "heading":
      return { ...base, text: "Write your heading here", fontSize: "28px", fontFamily: "", color: "#111827", textAlign: "center", fontWeight: "700", lineHeight: "1.3" };
    case "paragraph":
      return { ...base, text: "Start typing your message.", fontSize: "16px", fontFamily: "", color: "#111827", textAlign: "left", lineHeight: "1.6", fontWeight: "400" };
    case "button":
      return { ...base, text: "Learn more", url: "", bgColor: "#4F46E5", textColor: "#ffffff", fontSize: "16px", borderRadius: "6px", padding: "14px 32px", align: "center", fontWeight: "700", fullWidth: true, fontFamily: "", borderWidth: "0", borderColor: "#000000" };
    case "review":
      return {
        ...base,
        badgeText: "Quick feedback",
        heading: "How was your experience?",
        url: "",
        textAlign: "center",
        cardBgColor: "#FFF8E7",
        headingColor: "#1F2937",
        helperColor: "#6B7280",
        accentColor: "#F59E0B",
        badgeTextColor: "#F59E0B",
        borderColor: "#F3D19C",
        cardRadius: "18px",
        starSize: String(DEFAULT_REVIEW_STAR_SIZE),
        clickThroughMinRating: DEFAULT_REVIEW_CLICK_THROUGH_MIN_RATING,
        surveyTemplateId: "",
        surveyUrl: "",
      };
    case "divider":
      return { ...base, color: "#E5E7EB", thickness: "1px", dividerWidth: "100%", style: "solid" };
    case "spacer":
      return { ...base, spacerHeight: "40px" };
    case "social":
      return { ...base, align: "center", iconSize: "32", iconColor: "#111827", iconSpacing: "12", textColor: "#111827", textFontSize: "13px", textFontWeight: "500", textFontFamily: "", items: [
        { id: uid(), type: "icon", platform: "facebook", url: "", label: "Facebook", iconMode: "library", customIconSrc: "" },
        { id: uid(), type: "icon", platform: "instagram", url: "", label: "Instagram", iconMode: "library", customIconSrc: "" },
        { id: uid(), type: "icon", platform: "twitter", url: "", label: "Twitter", iconMode: "library", customIconSrc: "" },
      ] };
    case "logo":
      return { ...base, src: "", linkUrl: "", logoWidth: "150px", align: "center" };
    default:
      return base;
  }
};

const createColumnsBlock = (colCount) => {
  const cols = [];
  for (let i = 0; i < colCount; i++) cols.push({ id: uid(), blocks: [] });
  return { id: uid(), type: "columns", columns: cols, gap: "16", columnWidths: getDefaultColumnWidths(colCount) };
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
export default function ScratchEmailDesigner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams();
  const { userProfile } = useProfileContext();
  const { setToastMessage } = useToast();

  const isNewTemplate = templateId === "new";
  const templateData = location.state?.template;

  const { emailTemplates, updateTemplate, isUpdating, duplicateTemplateAsync, isDuplicating } =
    useMarketingEmailTemplates(userProfile?.organisation_id);

  const { senderDomains } = useMarketingSenderDomains(userProfile?.organisation_id);
  const { surveyTemplates = [], isLoading: isLoadingSurveyTemplates } =
    useSurveyTemplates(userProfile?.organisation_id);
  const activeSurveyTemplates = useMemo(
    () => (surveyTemplates || []).filter((template) => template.is_active !== false),
    [surveyTemplates]
  );

  const existingTemplate = !isNewTemplate && emailTemplates?.find(
    (t) => t.template_id === templateId
  );
  const activeTemplate = existingTemplate || templateData;
  const activeTemplateId = existingTemplate?.template_id || templateData?.template_id || (isNewTemplate ? null : templateId);

  /* ── state ── */
  const [blocks, setBlocks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [leftTab, setLeftTab] = useState("blocks");
  const [viewMode, setViewMode] = useState("desktop");
  const [templateName, setTemplateName] = useState(templateData?.name || "Untitled Template");
  const [emailSubject, setEmailSubject] = useState(templateData?.subject || "");
  const [editingName, setEditingName] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [discardAlertOpen, setDiscardAlertOpen] = useState(false);
  const [reviewLinkAlertOpen, setReviewLinkAlertOpen] = useState(false);
  const [sendTestOpen, setSendTestOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [testEmailError, setTestEmailError] = useState("");
  const [testSenderDomainId, setTestSenderDomainId] = useState("default");
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [pendingScratchAssets, setPendingScratchAssets] = useState({});
  const [invalidReviewCount, setInvalidReviewCount] = useState(0);
  const [firstInvalidReviewId, setFirstInvalidReviewId] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateForm, setDuplicateForm] = useState({ name: "", subject: "", purpose: "welcome" });

  // Email‐level design
  const [emailDesign, setEmailDesign] = useState(() => normalizeEmailDesign());
  const updateDesign = (u) => setEmailDesign((p) => ({ ...p, ...u }));
  const footerLines = emailDesign.footerLines || getFooterLines(emailDesign);

  // Drag state
  const [dragType, setDragType] = useState(null);      // "new" | "reorder" | "media-move"
  const [dragNewBlockType, setDragNewBlockType] = useState(null);
  const [dragBlockId, setDragBlockId] = useState(null);
  const [dragMediaSource, setDragMediaSource] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [mergeTargetBlockId, setMergeTargetBlockId] = useState(null);

  // Undo / redo
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const hasUnsavedChanges = useMemo(() => {
    if (!savedSnapshot) {
      return blocks.length > 0
        || templateName !== (templateData?.name || "Untitled Template")
        || emailSubject !== (templateData?.subject || "");
    }
    return JSON.stringify({ name: templateName, subject: emailSubject, blocks, design: emailDesign }) !== JSON.stringify(savedSnapshot);
  }, [blocks, emailDesign, emailSubject, savedSnapshot, templateData?.name, templateData?.subject, templateName]);

  const canvasRef = useRef(null);

  // Load existing scratch design_json when available
  useEffect(() => {
    const source = existingTemplate || templateData;
    if (!source) return;
    if (source.template_type === "predefined") {
      navigate(`/marketing/templates/editor/${source.template_id}`, { state: { template: source } });
      return;
    }
    const nextBlocks = source?.design_json?.scratchBlocks || [];
    const nextDesign = source?.design_json?.scratchDesign
      ? normalizeEmailDesign(source.design_json.scratchDesign)
      : normalizeEmailDesign();
    const nextName = source.name || "Untitled Template";
    const nextSubject = source.subject || "";

    setBlocks(nextBlocks);
    setEmailDesign(nextDesign);
    setTemplateName(nextName);
    setEmailSubject(nextSubject);
    setSavedSnapshot(createDesignerSnapshot(nextName, nextSubject, nextBlocks, nextDesign));
    setPendingScratchAssets({});
  }, [existingTemplate, navigate, templateData]);

  /* ── find selected block (supports nested columns) ── */
  const findBlock = (id) => {
    if (!id) return null;
    for (const b of blocks) {
      if (b.id === id) return b;
      if (b.type === "columns") {
        for (const col of b.columns) {
          for (const cb of col.blocks) {
            if (cb.id === id) return cb;
          }
        }
      }
    }
    return null;
  };
  const selected = findBlock(selectedId);

  const clearPendingScratchAsset = useCallback((assetKey) => {
    if (!assetKey) return;
    setPendingScratchAssets((prev) => {
      if (!prev[assetKey]) return prev;
      const next = { ...prev };
      delete next[assetKey];
      return next;
    });
  }, []);

  const handleScratchFileSelection = useCallback(async (file, onPreviewReady, currentAssetKey) => {
    if (!file) return;
    if (file.size > MAX_SCRATCH_IMAGE_SIZE_BYTES) {
      setToastMessage("Maximum file size is 1MB");
      return;
    }

    try {
      const previewSrc = await readFileAsDataUrl(file);
      const nextAssetKey = createScratchAssetKey();

      setPendingScratchAssets((prev) => {
        const next = { ...prev };
        if (currentAssetKey) delete next[currentAssetKey];
        next[nextAssetKey] = file;
        return next;
      });

      onPreviewReady(previewSrc, nextAssetKey);
    } catch (error) {
      console.error("Failed to load scratch asset:", error);
      setToastMessage("Failed to load image");
    }
  }, [setToastMessage]);

  const serializeScratchBlocksForSave = useCallback((blocksArr) => {
    const referencedAssetKeys = new Set();

    const serializeItem = (item) => {
      const nextItem = { ...item };

      if (
        nextItem.type === "icon"
        && nextItem.iconMode === "upload"
        && nextItem.customIconAssetKey
        && pendingScratchAssets[nextItem.customIconAssetKey]
      ) {
        referencedAssetKeys.add(nextItem.customIconAssetKey);
        nextItem.customIconSrc = buildScratchAssetPlaceholder(nextItem.customIconAssetKey);
      }

      delete nextItem.customIconAssetKey;
      return nextItem;
    };

    const serializeBlock = (block) => {
      const nextBlock = { ...block };

      if (
        (nextBlock.type === "image" || nextBlock.type === "logo")
        && nextBlock.srcAssetKey
        && pendingScratchAssets[nextBlock.srcAssetKey]
      ) {
        referencedAssetKeys.add(nextBlock.srcAssetKey);
        nextBlock.src = buildScratchAssetPlaceholder(nextBlock.srcAssetKey);
      }

      delete nextBlock.srcAssetKey;

      if (nextBlock.type === "social") {
        nextBlock.items = (nextBlock.items || []).map(serializeItem);
      }

      if (nextBlock.type === "columns") {
        nextBlock.columns = (nextBlock.columns || []).map((col) => ({
          ...col,
          blocks: (col.blocks || []).map(serializeBlock),
        }));
      }

      return nextBlock;
    };

    return {
      scratchBlocks: blocksArr.map(serializeBlock),
      referencedAssetKeys: Array.from(referencedAssetKeys),
    };
  }, [pendingScratchAssets]);

  const updateFooterLine = useCallback((lineId, updates) => {
    updateDesign({
      footerLines: footerLines.map((line) => (line.id === lineId ? { ...line, ...updates } : line)),
    });
  }, [footerLines]);

  const addFooterTextLine = useCallback(() => {
    const insertIndex = footerLines.findIndex((line) => line.type === "copyright");
    const nextLines = [...footerLines];
    nextLines.splice(insertIndex === -1 ? nextLines.length : insertIndex, 0, createFooterLine("custom"));
    updateDesign({ footerLines: nextLines });
  }, [footerLines]);

  const moveFooterLine = useCallback((lineId, direction) => {
    const currentIndex = footerLines.findIndex((line) => line.id === lineId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= footerLines.length) return;
    const nextLines = [...footerLines];
    [nextLines[currentIndex], nextLines[nextIndex]] = [nextLines[nextIndex], nextLines[currentIndex]];
    updateDesign({ footerLines: nextLines });
  }, [footerLines]);

  const removeFooterLine = useCallback((lineId) => {
    updateDesign({
      footerLines: footerLines.filter((line) => line.id !== lineId || line.type !== "custom"),
    });
  }, [footerLines]);

  /* ── history helpers ── */
  const pushHistory = useCallback((prev) => {
    setHistory((h) => [...h.slice(-40), prev]);
    setFuture([]);
  }, []);
  const undo = () => {
    if (!history.length) return;
    setFuture((f) => [...f, blocks]);
    setBlocks(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
  };
  const redo = () => {
    if (!future.length) return;
    setHistory((h) => [...h, blocks]);
    setBlocks(future[future.length - 1]);
    setFuture((f) => f.slice(0, -1));
  };

  /* ── block CRUD ── */
  const addBlockAt = (type, index) => {
    pushHistory(blocks);
    const b = defaultBlock(type);
    const n = [...blocks];
    n.splice(index, 0, b);
    setBlocks(n);
    setSelectedId(b.id);
  };
  const addBlock = (type) => addBlockAt(type, blocks.length);

  const updateBlock = (id, updates) => {
    pushHistory(blocks);
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id === id) return { ...b, ...updates };
        if (b.type === "columns") {
          return {
            ...b,
            columns: b.columns.map((col) => ({
              ...col,
              blocks: col.blocks.map((cb) => (cb.id === id ? { ...cb, ...updates } : cb)),
            })),
          };
        }
        return b;
      })
    );
  };

  const applyColumnLayoutPreset = (blockId, columnWidths) => {
    pushHistory(blocks);
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId || b.type !== "columns") return b;

        const nextBlock = { ...b, columnWidths: [...columnWidths] };

        return {
          ...nextBlock,
          columns: nextBlock.columns.map((col, colIndex) => ({
            ...col,
            blocks: col.blocks.map((child) => {
              if (child.type === "image") {
                const currentImageSize = String(child.imageSize || "").trim();
                const currentWidth = String(child.width || "").trim();
                const previousAutoSize = String(getEstimatedColumnMediaWidth(b, "image", colIndex));
                const shouldResize = (!currentImageSize && (!currentWidth || currentWidth === "100%"))
                  || (currentImageSize === previousAutoSize && (!currentWidth || currentWidth === ""));

                if (!shouldResize) return child;
                return {
                  ...child,
                  width: "",
                  imageSize: String(getEstimatedColumnMediaWidth(nextBlock, "image", colIndex)),
                };
              }

              if (child.type === "logo") {
                const currentLogoWidth = String(child.logoWidth || "").trim();
                const previousAutoSize = `${getEstimatedColumnMediaWidth(b, "logo", colIndex)}px`;
                const shouldResize = !currentLogoWidth || currentLogoWidth === previousAutoSize;

                if (!shouldResize) return child;
                return {
                  ...child,
                  logoWidth: `${getEstimatedColumnMediaWidth(nextBlock, "logo", colIndex)}px`,
                };
              }

              return child;
            }),
          })),
        };
      })
    );
  };

  const deleteBlock = (id) => {
    pushHistory(blocks);
    setBlocks((prev) => {
      const filtered = prev.filter((b) => b.id !== id);
      return filtered.map((b) => {
        if (b.type === "columns") {
          return { ...b, columns: b.columns.map((col) => ({ ...col, blocks: col.blocks.filter((cb) => cb.id !== id) })) };
        }
        return b;
      });
    });
    if (selectedId === id) setSelectedId(null);
  };

  const duplicateBlock = (id) => {
    pushHistory(blocks);
    const i = blocks.findIndex((b) => b.id === id);
    if (i < 0) return;
    const clone = JSON.parse(JSON.stringify(blocks[i]));
    clone.id = uid();
    if (clone.columns) clone.columns.forEach((c) => { c.id = uid(); c.blocks.forEach((cb) => { cb.id = uid(); }); });
    const n = [...blocks];
    n.splice(i + 1, 0, clone);
    setBlocks(n);
    setSelectedId(clone.id);
  };

  const moveBlock = (id, dir) => {
    pushHistory(blocks);
    const i = blocks.findIndex((b) => b.id === id);
    const ni = i + dir;
    if (ni < 0 || ni >= blocks.length) return;
    const n = [...blocks];
    [n[i], n[ni]] = [n[ni], n[i]];
    setBlocks(n);
  };

  /* ── column‐block management ── */
  const addBlockToColumn = (blockId, colId, blockType) => {
    pushHistory(blocks);
    const nb = defaultBlock(blockType);
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId || b.type !== "columns") return b;
        const targetColumnIndex = b.columns.findIndex((col) => col.id === colId);
        const nextBlock = isImageLikeBlockType(blockType)
          ? normalizeMediaBlockForColumn(nb, b, targetColumnIndex)
          : nb;
        return { ...b, columns: b.columns.map((col) => (col.id === colId ? { ...col, blocks: [...col.blocks, nextBlock] } : col)) };
      })
    );
    setSelectedId(nb.id);
  };

  const updateColumnBlock = (parentId, colId, childId, updates) => {
    pushHistory(blocks);
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== parentId || b.type !== "columns") return b;
        return { ...b, columns: b.columns.map((col) => (col.id === colId ? { ...col, blocks: col.blocks.map((cb) => (cb.id === childId ? { ...cb, ...updates } : cb)) } : col)) };
      })
    );
  };

  const deleteColumnBlock = (parentId, colId, childId) => {
    pushHistory(blocks);
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== parentId || b.type !== "columns") return b;
        return { ...b, columns: b.columns.map((col) => (col.id === colId ? { ...col, blocks: col.blocks.filter((cb) => cb.id !== childId) } : col)) };
      })
    );
    if (selectedId === childId) setSelectedId(parentId);
  };

  const moveColumnBlock = (parentId, colId, childId, dir) => {
    pushHistory(blocks);
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== parentId || b.type !== "columns") return b;
        return {
          ...b,
          columns: b.columns.map((col) => {
            if (col.id !== colId) return col;
            const idx = col.blocks.findIndex((cb) => cb.id === childId);
            const ni = idx + dir;
            if (ni < 0 || ni >= col.blocks.length) return col;
            const nblocks = [...col.blocks];
            [nblocks[idx], nblocks[ni]] = [nblocks[ni], nblocks[idx]];
            return { ...col, blocks: nblocks };
          }),
        };
      })
    );
  };

  const extractDraggedMediaBlock = (blocksArr, source) => {
    if (!source) return null;

    if (source.container === "top-level") {
      const removedIndex = blocksArr.findIndex((block) => block.id === source.blockId && isImageLikeBlockType(block.type));
      if (removedIndex < 0) return null;

      const movedBlock = cloneScratchValue(blocksArr[removedIndex]);
      const nextBlocks = [...blocksArr];
      nextBlocks.splice(removedIndex, 1);
      return { movedBlock, nextBlocks, removedIndex };
    }

    let movedBlock = null;
    let removedIndex = -1;

    const nextBlocks = blocksArr.map((block) => {
      if (block.id !== source.parentId || block.type !== "columns") return block;

      return {
        ...block,
        columns: block.columns.map((col) => {
          if (col.id !== source.colId) return col;

          const childIndex = col.blocks.findIndex((child) => child.id === source.blockId && isImageLikeBlockType(child.type));
          if (childIndex < 0) return col;

          movedBlock = cloneScratchValue(col.blocks[childIndex]);
          removedIndex = childIndex;

          const nextColumnBlocks = [...col.blocks];
          nextColumnBlocks.splice(childIndex, 1);
          return { ...col, blocks: nextColumnBlocks };
        }),
      };
    });

    if (!movedBlock) return null;
    return { movedBlock, nextBlocks, removedIndex };
  };

  const insertMediaBlockAtTopLevel = (blocksArr, movedBlock, index) => {
    const nextBlocks = [...blocksArr];
    const safeIndex = Math.max(0, Math.min(index, nextBlocks.length));
    nextBlocks.splice(safeIndex, 0, movedBlock);
    return nextBlocks;
  };

  const insertMediaBlockIntoColumn = (blocksArr, parentId, colId, movedBlock, index) => (
    blocksArr.map((parentBlock) => {
      if (parentBlock.id !== parentId || parentBlock.type !== "columns") return parentBlock;

      return {
        ...parentBlock,
        columns: parentBlock.columns.map((col) => {
          if (col.id !== colId) return col;

          const nextColumnBlocks = [...col.blocks];
          const safeIndex = Math.max(0, Math.min(index, nextColumnBlocks.length));
          nextColumnBlocks.splice(safeIndex, 0, movedBlock);
          return { ...col, blocks: nextColumnBlocks };
        }),
      };
    })
  );

  const getEstimatedColumnMediaWidth = (parentBlock, mediaType, columnIndex = 0) => {
    const contentWidth = Math.min(parseInt(emailDesign.contentWidth, 10) || 600, 600);
    const columnCount = Math.max(parentBlock?.columns?.length || 1, 1);
    const gap = parseInt(parentBlock?.gap, 10) || 16;
    const sectionPadding = parseInt(parentBlock?.sectionPadding, 10) || 0;
    const columnWidths = getNormalizedColumnWidths(parentBlock);
    const widthShare = (columnWidths[columnIndex] || (100 / columnCount)) / 100;
    const availableWidth = Math.max(120, contentWidth - (sectionPadding * 2) - (gap * Math.max(columnCount - 1, 0)));
    const columnWidth = Math.floor(availableWidth * widthShare);
    const innerPadding = mediaType === "logo" ? 24 : 16;
    return Math.max(mediaType === "logo" ? 60 : 48, columnWidth - innerPadding);
  };

  const getColumnMediaWidthValue = (block, parentBlock, columnIndex = 0) => {
    if (block.type === "image") {
      if ((block.imageSize && String(block.imageSize).trim() !== "") || (block.width && block.width !== "100%")) {
        return getImageWidthValue(block);
      }
      return `${getEstimatedColumnMediaWidth(parentBlock, "image", columnIndex)}px`;
    }

    if (block.type === "logo") {
      const logoWidth = String(block.logoWidth || "").trim();
      if (logoWidth && logoWidth !== "100%") return logoWidth;
      return `${getEstimatedColumnMediaWidth(parentBlock, "logo", columnIndex)}px`;
    }

    return "100%";
  };

  const normalizeMediaBlockForColumn = (block, parentBlock, columnIndex = 0) => {
    const nextBlock = cloneScratchValue(block);
    nextBlock.sectionBg = "transparent";
    nextBlock.sectionPadding = "0";
    delete nextBlock.sectionHeight;
    delete nextBlock.sectionMaxWidth;

    if (nextBlock.type === "image") {
      nextBlock.fullBleed = false;
      nextBlock.width = "";
      nextBlock.imageSize = String(getEstimatedColumnMediaWidth(parentBlock, "image", columnIndex));
    }

    if (nextBlock.type === "logo") {
      nextBlock.logoWidth = `${getEstimatedColumnMediaWidth(parentBlock, "logo", columnIndex)}px`;
    }

    return nextBlock;
  };

  const moveDraggedMediaBlockToCanvas = (targetIndex) => {
    if (!dragMediaSource) return false;

    pushHistory(blocks);
    setBlocks((prev) => {
      const extracted = extractDraggedMediaBlock(prev, dragMediaSource);
      if (!extracted) return prev;

      const adjustedIndex = dragMediaSource.container === "top-level" && extracted.removedIndex < targetIndex
        ? targetIndex - 1
        : targetIndex;

      return insertMediaBlockAtTopLevel(extracted.nextBlocks, extracted.movedBlock, adjustedIndex);
    });
    setSelectedId(dragMediaSource.blockId);
    return true;
  };

  const moveDraggedMediaBlockToColumn = (parentId, colId, targetIndex) => {
    if (!dragMediaSource) return false;

    pushHistory(blocks);
    setBlocks((prev) => {
      const extracted = extractDraggedMediaBlock(prev, dragMediaSource);
      if (!extracted) return prev;

      const adjustedIndex = (
        dragMediaSource.container === "column"
        && dragMediaSource.parentId === parentId
        && dragMediaSource.colId === colId
        && extracted.removedIndex < targetIndex
      )
        ? targetIndex - 1
        : targetIndex;

      const parentBlock = extracted.nextBlocks.find((block) => block.id === parentId && block.type === "columns");
      const targetColumnIndex = parentBlock?.columns?.findIndex((column) => column.id === colId) ?? 0;

      return insertMediaBlockIntoColumn(
        extracted.nextBlocks,
        parentId,
        colId,
        normalizeMediaBlockForColumn(extracted.movedBlock, parentBlock, targetColumnIndex),
        adjustedIndex
      );
    });
    setSelectedId(dragMediaSource.blockId);
    return true;
  };

  const dropIntoColumn = (parentId, colId, blockType, dropIdx) => {
    pushHistory(blocks);
    const nb = defaultBlock(blockType);
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== parentId || b.type !== "columns") return b;
        const targetColumnIndex = b.columns.findIndex((col) => col.id === colId);
        const nextBlock = isImageLikeBlockType(blockType)
          ? normalizeMediaBlockForColumn(nb, b, targetColumnIndex)
          : nb;
        return {
          ...b,
          columns: b.columns.map((col) => {
            if (col.id !== colId) return col;
            const nblocks = [...col.blocks];
            nblocks.splice(dropIdx ?? col.blocks.length, 0, nextBlock);
            return { ...col, blocks: nblocks };
          }),
        };
      })
    );
    setSelectedId(nb.id);
  };

  const replaceTopLevelMediaBlockWithColumns = (targetId, newBlockType) => {
    if (!isImageLikeBlockType(newBlockType)) return false;

    const targetIndex = blocks.findIndex((block) => block.id === targetId && isImageLikeBlockType(block.type));
    if (targetIndex < 0) return false;

    const targetBlock = blocks[targetIndex];
    const existingMediaBlock = cloneScratchValue(targetBlock);

    const newMediaBlock = defaultBlock(newBlockType);
    const columnsBlock = {
      id: uid(),
      type: "columns",
      sectionBg: targetBlock.sectionBg || "transparent",
      sectionPadding: targetBlock.sectionPadding || "0",
      ...(targetBlock.sectionHeight ? { sectionHeight: targetBlock.sectionHeight } : {}),
      ...(targetBlock.sectionMaxWidth ? { sectionMaxWidth: targetBlock.sectionMaxWidth } : {}),
      gap: "16",
      columnWidths: getDefaultColumnWidths(2),
      columns: [
        { id: uid(), blocks: [] },
        { id: uid(), blocks: [] },
      ],
    };
    columnsBlock.columns[0].blocks = [normalizeMediaBlockForColumn(existingMediaBlock, columnsBlock, 0)];
    columnsBlock.columns[1].blocks = [normalizeMediaBlockForColumn(newMediaBlock, columnsBlock, 1)];

    pushHistory(blocks);
    const nextBlocks = [...blocks];
    nextBlocks.splice(targetIndex, 1, columnsBlock);
    setBlocks(nextBlocks);
    setSelectedId(newMediaBlock.id);
    return true;
  };

  /* ── drag handlers ── */
  const resolveMergeTargetBlockId = (event) => {
    if (!(dragType === "new" && isImageLikeBlockType(dragNewBlockType))) return null;

    const targetElement = event.target instanceof Element
      ? event.target.closest("[data-top-level-block='true']")
      : null;

    if (!targetElement || !canvasRef.current?.contains(targetElement)) return null;

    const targetType = targetElement.getAttribute("data-block-type");
    if (!isImageLikeBlockType(targetType)) return null;

    return targetElement.getAttribute("data-block-id");
  };

  const resetDragState = () => {
    setDragType(null);
    setDragNewBlockType(null);
    setDragBlockId(null);
    setDragMediaSource(null);
    setDropIndex(null);
    setIsDraggingOver(false);
    setMergeTargetBlockId(null);
  };

  const handleSidebarDragStart = (e, type) => {
    setDragType("new");
    setDragNewBlockType(type);
    e.dataTransfer.setData("text/plain", `new:${type}`);
    e.dataTransfer.effectAllowed = "copy";
  };
  const handleCanvasDragStart = (e, id, blockType) => {
    if (isImageLikeBlockType(blockType)) {
      setDragType("media-move");
      setDragMediaSource({ container: "top-level", blockId: id });
    } else {
      setDragType("reorder");
      setDragMediaSource(null);
    }
    setDragBlockId(id);
    e.dataTransfer.setData("text/plain", `${isImageLikeBlockType(blockType) ? "media" : "reorder"}:${id}`);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleColumnMediaDragStart = (e, parentId, colId, childId) => {
    setDragType("media-move");
    setDragBlockId(childId);
    setDragMediaSource({ container: "column", parentId, colId, blockId: childId });
    e.dataTransfer.setData("text/plain", `media:${childId}`);
    e.dataTransfer.effectAllowed = "move";
  };
  const computeDropIndex = (e) => {
    const el = canvasRef.current;
    if (!el) return blocks.length;
    const bls = el.querySelectorAll("[data-block-idx]");
    for (let i = 0; i < bls.length; i++) {
      const r = bls[i].getBoundingClientRect();
      if (e.clientY < r.top + r.height / 2) return i;
    }
    return blocks.length;
  };
  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = dragType === "new" ? "copy" : "move";
    setIsDraggingOver(true);
    const nextMergeTargetId = resolveMergeTargetBlockId(e);
    setMergeTargetBlockId(nextMergeTargetId);
    if (nextMergeTargetId) {
      setDropIndex(null);
      return;
    }
    setDropIndex(computeDropIndex(e));
  };
  const handleCanvasDragLeave = (e) => {
    if (!canvasRef.current?.contains(e.relatedTarget)) {
      setIsDraggingOver(false);
      setDropIndex(null);
      setMergeTargetBlockId(null);
    }
  };
  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const nextMergeTargetId = resolveMergeTargetBlockId(e) || mergeTargetBlockId;
    setIsDraggingOver(false);
    if (nextMergeTargetId && dragType === "new" && isImageLikeBlockType(dragNewBlockType)) {
      replaceTopLevelMediaBlockWithColumns(nextMergeTargetId, dragNewBlockType);
      resetDragState();
      return;
    }
    const idx = dropIndex ?? blocks.length;
    setDropIndex(null);
    if (dragType === "new" && dragNewBlockType) {
      if (dragNewBlockType.startsWith("columns:")) {
        const count = parseInt(dragNewBlockType.split(":")[1]) || 2;
        pushHistory(blocks);
        const cb = createColumnsBlock(count);
        const n = [...blocks];
        n.splice(idx, 0, cb);
        setBlocks(n);
        setSelectedId(cb.id);
      } else {
        addBlockAt(dragNewBlockType, idx);
      }
    } else if (dragType === "media-move" && dragMediaSource) {
      moveDraggedMediaBlockToCanvas(idx);
    } else if (dragType === "reorder" && dragBlockId) {
      pushHistory(blocks);
      const fi = blocks.findIndex((b) => b.id === dragBlockId);
      if (fi < 0) return;
      const n = [...blocks];
      const [m] = n.splice(fi, 1);
      n.splice(idx > fi ? idx - 1 : idx, 0, m);
      setBlocks(n);
    }
    resetDragState();
  };
  const handleDragEnd = () => resetDragState();

  /* ── generate MJML + HTML from scratch blocks ── */
  const generateScratchMjml = (blocksArr, design) => {
    const gFont = design.globalFontFamily || "Arial, sans-serif";
    const contentWidth = Math.min(parseInt(design.contentWidth) || 600, 600);
    const contentBg = design.contentBgColor || "#FFFFFF";
    const bodyBg = design.bgColor || "#F3F4F6";
    const contentRadius = parseInt(design.contentBorderRadius) || 0;
    const contentPad = parseInt(design.contentPadding) || 0;

    const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const normalizePreviewText = (value) => String(value || "").replace(/\s+/g, " ").trim();

    const extractPreviewText = (sourceBlocks, { includeFallbackText = false } = {}) => {
      const chunks = [];

      const appendChunk = (value) => {
        const normalizedValue = normalizePreviewText(value);
        if (normalizedValue) chunks.push(normalizedValue);
      };

      const visitBlock = (block) => {
        if (!block) return;

        switch (block.type) {
          case "heading":
          case "paragraph":
            appendChunk(block.text);
            break;
          case "columns":
            (block.columns || []).forEach((col) => (col.blocks || []).forEach(visitBlock));
            break;
          case "button":
            if (includeFallbackText) appendChunk(block.text);
            break;
          case "review":
            if (includeFallbackText) {
              appendChunk(block.heading);
              appendChunk(block.helperText);
            }
            break;
          case "social":
            if (includeFallbackText) {
              (block.items || []).forEach((item) => {
                if (item.type === "text") appendChunk(item.label);
              });
            }
            break;
          default:
            break;
        }
      };

      (sourceBlocks || []).forEach(visitBlock);
      return normalizePreviewText(chunks.join(" "));
    };

    const previewText = esc(
      (
        extractPreviewText(blocksArr)
        || extractPreviewText(blocksArr, { includeFallbackText: true })
        || templateName
        || userProfile?.organisation_name
        || ""
      ).slice(0, 180)
    );

    const buildReviewBlockMjml = (block, compact = false) => {
      const badgeText = esc(block.badgeText || "");
      const heading = esc(block.heading || "How did we do today?");
      const helperText = esc(block.helperText || "Tap the stars to leave a quick review.").replace(/\n/g, "<br/>");
      const cardBgColor = block.cardBgColor || "#FFF8E7";
      const headingColor = block.headingColor || "#1F2937";
      const helperColor = block.helperColor || "#6B7280";
      const accentColor = block.accentColor || "#F59E0B";
      const badgeTextColor = block.badgeTextColor || accentColor;
      const borderColor = block.borderColor || "#F3D19C";
      const cardRadius = block.cardRadius || "18px";
      const textAlign = block.textAlign || "center";
      const titleSize = compact ? "18px" : "30px";
      const helperSize = compact ? "11px" : "13px";
      const badgeSize = compact ? "10px" : "11px";
      const badgePadding = compact ? "4px 8px" : "5px 10px";
      const cardPadding = compact ? "14px 14px 16px" : "20px 22px 22px";
      const outerPadding = compact ? "8px 12px" : "18px 30px";
      const starSize = `${getReviewStarSize(block, compact)}px`;
      const starSpacing = compact ? "6px" : "8px";
      const cardShadow = getReviewCardShadow(compact);
      const clickThroughMinRating = getReviewClickThroughMinRating(block);
      const cardTableStyles = [
        "width:100%",
        "border-collapse:separate",
        "border-spacing:0",
        "mso-table-lspace:0pt",
        "mso-table-rspace:0pt",
        `background:${cardBgColor}`,
        `border-radius:${cardRadius}`,
        cardShadow !== "none" ? `box-shadow:${cardShadow}` : "",
      ].filter(Boolean).join(";");
      const href = esc(ensureAbsoluteUrl(block.url) || "#");
      const surveyHref = esc(ensureAbsoluteUrl(getReviewSurveyUrl(block)) || "#");
      const starsMarkup = Array.from({ length: REVIEW_STAR_COUNT }, (_, index) => {
        const marginRight = index === REVIEW_STAR_COUNT - 1 ? "0" : starSpacing;
        const rating = index + 1;
        const star = `<span style="display:inline-block;color:${accentColor};font-size:${starSize};line-height:1;margin-right:${marginRight};">&#9733;</span>`;
        if (rating >= clickThroughMinRating && href !== "#") {
          return `<a href="${href}" style="display:inline-block;text-decoration:none;color:${accentColor};line-height:1;">${star}</a>`;
        }
        if (rating < clickThroughMinRating && surveyHref !== "#") {
          return `<a href="${surveyHref}" style="display:inline-block;text-decoration:none;color:${accentColor};line-height:1;">${star}</a>`;
        }
        return star;
      }).join("");

      return `
      <mj-table padding="${outerPadding}">
        <tr>
          <td style="padding:0;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="${cardTableStyles}">
              <tr>
                <td style="background:${cardBgColor};border:1px solid ${borderColor};border-radius:${cardRadius};padding:${cardPadding};text-align:${textAlign};">
                  ${badgeText ? `<div style="margin-bottom:${compact ? "10px" : "12px"};"><span style="display:inline-block;padding:${badgePadding};border-radius:999px;background:#FFFFFF;border:1px solid ${borderColor};color:${badgeTextColor};font-family:${gFont};font-size:${badgeSize};font-weight:700;letter-spacing:0.08em;text-transform:uppercase;">${badgeText}</span></div>` : ""}
                  <div style="font-family:${gFont};font-size:${titleSize};line-height:1.2;font-weight:700;color:${headingColor};">${heading}</div>
                  <div style="padding:${compact ? "10px" : "12px"} 0 ${compact ? "8px" : "10px"};font-size:0;line-height:0;">${starsMarkup}</div>
                  ${helperText ? `<div style="font-family:${gFont};font-size:${helperSize};line-height:1.6;color:${helperColor};">${helperText}</div>` : ""}
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </mj-table>`;
    };

    const buildSocialTableMjml = (block, compact = false) => {
      const items = block.items || [];
      if (!items.length) return "";

      const spacing = compact ? 8 : (parseInt(block.iconSpacing, 10) || 12);
      const cellPadding = Math.round(spacing / 2);
      const iconSize = block.iconSize || "32";
      const textColor = block.textColor || block.iconColor || "#111827";
      const textFontSize = block.textFontSize || "13px";
      const textFontWeight = block.textFontWeight || "500";
      const textFontFamily = block.textFontFamily || gFont;
      const alignment = block.align || "center";
      const tableMargin = alignment === "left"
        ? "0 auto 0 0"
        : alignment === "right" ? "0 0 0 auto" : "0 auto";

      const itemCells = items.map((item) => {
        if (item.type === "spacer") {
          return `<td style="width:${item.width || "16"}px;font-size:0;line-height:0;">&nbsp;</td>`;
        }
        if (item.type === "text") {
          return `<td style="padding:0 ${cellPadding}px;"><a href="${esc(item.url || "#")}" style="color:${textColor};font-size:${textFontSize};font-weight:${textFontWeight};font-family:${textFontFamily};text-decoration:none;">${esc(item.label || "Link")}</a></td>`;
        }

        const iconSrc = getSocialIconEmailSrc(item);
        if (!iconSrc) return "";
        return `<td style="padding:0 ${cellPadding}px;"><a href="${esc(item.url || "#")}" style="display:block;text-decoration:none;"><img src="${esc(iconSrc)}" width="${iconSize}" height="${iconSize}" alt="${esc(item.label || item.platform || "icon")}" style="display:block;border:0;outline:none;object-fit:contain;" /></a></td>`;
      }).join("");

      if (!itemCells) return "";
      return `<mj-table padding="${compact ? "8px 12px" : "16px 30px"}"><tr><td align="${alignment}"><table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:${tableMargin};"><tr>${itemCells}</tr></table></td></tr></mj-table>`;
    };

    const blockToMjml = (block) => {
      const sectionAttrs = [
        `background-color="${block.sectionBg && block.sectionBg !== "transparent" ? block.sectionBg : "transparent"}"`,
        block.sectionPadding && block.sectionPadding !== "0" ? `padding="${block.sectionPadding}px"` : `padding="0"`,
        block.sectionMaxWidth ? `css-class="scratch-section-maxw"` : "",
      ].filter(Boolean).join(" ");

      switch (block.type) {
        case "heading":
          return `
  <mj-section ${sectionAttrs}>
    <mj-column>
      <mj-text font-size="${block.fontSize || "28px"}" font-weight="${block.fontWeight || "700"}" font-family="${block.fontFamily || gFont}" color="${block.color || "#111827"}" align="${block.textAlign || "center"}" line-height="${block.lineHeight || "1.3"}" padding="20px 30px">${esc(block.text)}</mj-text>
    </mj-column>
  </mj-section>`;

        case "paragraph":
          return `
  <mj-section ${sectionAttrs}>
    <mj-column>
      <mj-text font-size="${block.fontSize || "16px"}" font-weight="${block.fontWeight || "400"}" font-family="${block.fontFamily || gFont}" color="${block.color || "#111827"}" align="${block.textAlign || "left"}" line-height="${block.lineHeight || "1.6"}" padding="10px 30px">${esc(block.text).replace(/\n/g, "<br/>")}</mj-text>
    </mj-column>
  </mj-section>`;

        case "image":
          if (!block.src) return "";
          return `
  <mj-section ${sectionAttrs}>
    <mj-column>
      <mj-image src="${block.src}" alt="${esc(block.alt)}" width="${getImageWidthValue(block)}" align="${block.align || "center"}" border-radius="${block.borderRadius || "0"}" padding="${block.fullBleed ? "0" : "10px 30px"}" href="${esc(getMediaHrefValue(block))}" />
    </mj-column>
  </mj-section>`;

        case "button": {
          const pad = block.padding || "14px 32px";
          return `
  <mj-section ${sectionAttrs}>
    <mj-column>
      <mj-button background-color="${block.bgColor || "#4F46E5"}" color="${block.textColor || "#ffffff"}" font-size="${block.fontSize || "16px"}" font-weight="${block.fontWeight || "700"}" font-family="${block.fontFamily || gFont}" border-radius="${block.borderRadius || "6px"}" inner-padding="${pad}" align="${block.align || "center"}" href="${block.url || "#"}"${parseInt(block.borderWidth) > 0 ? ` border="${block.borderWidth}px solid ${block.borderColor || "#000"}"` : ""}${block.fullWidth ? ' width="100%"' : ""}>${esc(block.text)}</mj-button>
    </mj-column>
  </mj-section>`;
        }

        case "review":
          return `
  <mj-section ${sectionAttrs}>
    <mj-column>
${buildReviewBlockMjml(block)}
    </mj-column>
  </mj-section>`;

        case "divider":
          return `
  <mj-section ${sectionAttrs}>
    <mj-column>
      <mj-divider border-color="${block.color || "#E5E7EB"}" border-width="${block.thickness || "1px"}" border-style="${block.style || "solid"}" width="${block.dividerWidth || "100%"}" padding="8px 30px" />
    </mj-column>
  </mj-section>`;

        case "spacer":
          return `
  <mj-section ${sectionAttrs}>
    <mj-column>
      <mj-spacer height="${block.spacerHeight || "40px"}" />
    </mj-column>
  </mj-section>`;

        case "logo":
          if (!block.src) return "";
          return `
  <mj-section ${sectionAttrs}>
    <mj-column>
      <mj-image src="${block.src}" alt="Logo" width="${block.logoWidth || "150px"}" align="${block.align || "center"}" padding="16px 30px" href="${esc(getMediaHrefValue(block))}" />
    </mj-column>
  </mj-section>`;

        case "social": {
          const socialTable = buildSocialTableMjml(block);
          if (!socialTable) return "";
          return `
  <mj-section ${sectionAttrs}>
    <mj-column>
      ${socialTable}
    </mj-column>
  </mj-section>`;
        }

        case "columns": {
          const cols = block.columns || [];
          const columnWidths = getNormalizedColumnWidths(block);
          const colMjml = cols.map((col, colIndex) => {
            const childMjml = (col.blocks || []).map((child) => {
              switch (child.type) {
                case "heading":
                  return `      <mj-text font-size="${child.fontSize || "22px"}" font-weight="${child.fontWeight || "700"}" font-family="${child.fontFamily || gFont}" color="${child.color || "#111827"}" align="${child.textAlign || "center"}" line-height="${child.lineHeight || "1.3"}" padding="8px 12px">${esc(child.text)}</mj-text>`;
                case "paragraph":
                  return `      <mj-text font-size="${child.fontSize || "14px"}" font-weight="${child.fontWeight || "400"}" font-family="${child.fontFamily || gFont}" color="${child.color || "#111827"}" align="${child.textAlign || "center"}" line-height="${child.lineHeight || "1.6"}" padding="6px 12px">${esc(child.text).replace(/\n/g, "<br/>")}</mj-text>`;
                case "image":
                  return child.src ? `      <mj-image src="${child.src}" alt="${esc(child.alt)}" width="${getColumnMediaWidthValue(child, block, colIndex)}" align="${child.align || "center"}" border-radius="${child.borderRadius || "0"}" padding="4px 8px" href="${esc(getMediaHrefValue(child))}" />` : "";
                case "button":
                  return `      <mj-button background-color="${child.bgColor || "#4F46E5"}" color="${child.textColor || "#ffffff"}" font-size="${child.fontSize || "14px"}" font-weight="${child.fontWeight || "600"}" font-family="${child.fontFamily || gFont}" border-radius="${child.borderRadius || "6px"}" inner-padding="${child.padding || "10px 20px"}" align="${child.align || "center"}" href="${child.url || "#"}">${esc(child.text)}</mj-button>`;
                case "review":
                  return buildReviewBlockMjml(child, true);
                case "divider":
                  return `      <mj-divider border-color="${child.color || "#E5E7EB"}" border-width="${child.thickness || "1px"}" border-style="${child.style || "solid"}" padding="4px 8px" />`;
                case "spacer":
                  return `      <mj-spacer height="${child.spacerHeight || "20px"}" />`;
                case "logo":
                  return child.src ? `      <mj-image src="${child.src}" alt="Logo" width="${getColumnMediaWidthValue(child, block, colIndex)}" align="${child.align || "center"}" padding="8px 12px" href="${esc(getMediaHrefValue(child))}" />` : "";
                case "social": {
                  return buildSocialTableMjml(child, true);
                }
                default:
                  return "";
              }
            }).filter(Boolean).join("\n");
            return `    <mj-column width="${formatColumnWidthPercent(columnWidths[colIndex] || (100 / Math.max(cols.length || 1, 1)))}" padding="0" vertical-align="middle">\n${childMjml || `      <mj-text padding="8px">&#160;</mj-text>`}\n    </mj-column>`;
          }).join("\n");
          return `
  <mj-section ${sectionAttrs}>
${colMjml}
  </mj-section>`;
        }

        default:
          return "";
      }
    };

    const bodyContent = blocksArr.map(blockToMjml).filter(Boolean).join("");

    const orgNameRaw = userProfile?.organisation_name || templateName || "Your Brand";
    const footerDesign = normalizeEmailDesign(design);
    const footerBgColor = footerDesign.footerBgColor || DEFAULT_EMAIL_DESIGN.footerBgColor;
    const footerTextColor = footerDesign.footerTextColor || DEFAULT_EMAIL_DESIGN.footerTextColor;
    const footerLinkColor = footerDesign.footerLinkColor || DEFAULT_EMAIL_DESIGN.footerLinkColor;
    const footerFontSize = getFooterFontSize(footerDesign);
    const footerLineHeight = Math.max(18, Math.round(footerFontSize * 1.5));
    const footerLineMjml = getFooterLines(footerDesign).map((line, index) => {
      const padding = index === 0 ? "0" : "8px 0 0";

      if (line.type === "browser") {
        const linkText = esc(line.linkText || FOOTER_LINE_DEFAULTS.browser.linkText);
        return `<mj-text align="center" font-size="${footerFontSize}px" color="${footerTextColor}" font-family="${gFont}" padding="${padding}" line-height="${footerLineHeight}px"><a href="{{marketing_browser_view_url}}" style="color: ${footerLinkColor}; text-decoration: underline;">${linkText}</a></mj-text>`;
      }

      if (line.type === "preferences") {
        const introText = esc(resolveFooterTemplateText(line.introText, orgNameRaw)).replace(/\n/g, "<br/>");
        const preferencesText = esc(line.preferencesText || FOOTER_LINE_DEFAULTS.preferences.preferencesText);
        const separatorText = esc(line.separatorText || FOOTER_LINE_DEFAULTS.preferences.separatorText);
        const unsubscribeText = esc(line.unsubscribeText || FOOTER_LINE_DEFAULTS.preferences.unsubscribeText);

        return `${introText ? `<mj-text align="center" font-size="${footerFontSize}px" color="${footerTextColor}" font-family="${gFont}" padding="${padding}" line-height="${footerLineHeight}px">${introText}</mj-text>` : ""}<mj-text align="center" font-size="${footerFontSize}px" color="${footerTextColor}" font-family="${gFont}" padding="${introText ? FOOTER_SPLIT_LINE_GAP : padding}" line-height="${footerLineHeight}px"><a href="{{marketing_preferences_url}}" style="color: ${footerLinkColor}; text-decoration: underline;">${preferencesText}</a>${separatorText}<a href="{{marketing_unsubscribe_url}}" style="color: ${footerLinkColor}; text-decoration: underline;">${unsubscribeText}</a></mj-text>`;
      }

      const lineText = esc(resolveFooterTemplateText(line.text, orgNameRaw)).replace(/\n/g, "<br/>");
      if (!lineText.trim()) return "";

      return `<mj-text align="center" font-size="${footerFontSize}px" color="${footerTextColor}" font-family="${gFont}" padding="${padding}" line-height="${footerLineHeight}px">${lineText}</mj-text>`;
    }).filter(Boolean).join("\n");
    const footerMjml = `
  <mj-section padding="28px 30px 24px" background-color="${footerBgColor}">
    <mj-column>
      ${footerLineMjml}
    </mj-column>
  </mj-section>`;

    return `<mjml>
  <mj-head>
    ${previewText ? `<mj-preview>${previewText}</mj-preview>` : ""}
    <mj-attributes>
      <mj-all font-family="${gFont}" />
      <mj-body background-color="${bodyBg}" />
    </mj-attributes>
    ${contentRadius > 0 ? `<mj-style>.scratch-body-wrapper { border-radius: ${contentRadius}px; overflow: hidden; }</mj-style>` : ""}
  </mj-head>
  <mj-body background-color="${bodyBg}" width="${contentWidth}px">
    <mj-wrapper background-color="${contentBg}" padding="${contentPad}px" ${contentRadius > 0 ? 'css-class="scratch-body-wrapper"' : ""}>
${bodyContent}
${footerMjml}
    </mj-wrapper>
  </mj-body>
</mjml>`;
  };

  /* ── save ── */
  const handleSave = async () => {
    if (!activeTemplateId || !activeTemplate) return;

    const invalidReviewBlocks = getReviewBlocksMissingLinks(blocks);
    if (invalidReviewBlocks.length) {
      setInvalidReviewCount(invalidReviewBlocks.length);
      setFirstInvalidReviewId(invalidReviewBlocks[0].id);
      setSelectedId(invalidReviewBlocks[0].id);
      setReviewLinkAlertOpen(true);
      return;
    }

    const { scratchBlocks: serializedBlocks, referencedAssetKeys } = serializeScratchBlocksForSave(blocks);
    let scratchBlocks;
    try {
      scratchBlocks = await prepareScratchSocialIconsForEmail(serializedBlocks);
    } catch (error) {
      console.error("Failed to prepare social icons for email:", error);
      setToastMessage("Failed to prepare social icons for email");
      return;
    }

    const designJson = {
      scratchBlocks,
      scratchDesign: emailDesign,
      designMode: "scratch",
    };

    const mjmlOutput = generateScratchMjml(scratchBlocks, emailDesign);

    const hasPendingAssetUploads = referencedAssetKeys.length > 0;
    let requestData;

    if (hasPendingAssetUploads) {
      requestData = new FormData();
      requestData.append("name", templateName);
      requestData.append("subject", emailSubject);
      requestData.append("purpose", activeTemplate.purpose || "");
      requestData.append("template_type", "scratch");
      requestData.append("mjml_content", mjmlOutput);
      requestData.append("design_json", JSON.stringify(designJson));

      referencedAssetKeys.forEach((assetKey) => {
        const file = pendingScratchAssets[assetKey];
        if (file) {
          requestData.append(buildScratchAssetFieldName(assetKey), file);
        }
      });
    } else {
      requestData = {
        name: templateName,
        subject: emailSubject,
        purpose: activeTemplate.purpose,
        template_type: "scratch",
        mjml_content: mjmlOutput,
        design_json: designJson,
      };
    }

    const payload = {
      templateId: activeTemplateId,
      data: requestData,
    };

    try {
      const response = await new Promise((resolve, reject) => {
        updateTemplate(payload, {
          onSuccess: resolve,
          onError: reject,
        });
      });

      const savedTemplate = response?.data || response;
      const nextBlocks = savedTemplate?.design_json?.scratchBlocks || scratchBlocks;
      const nextDesign = savedTemplate?.design_json?.scratchDesign
        ? normalizeEmailDesign(savedTemplate.design_json.scratchDesign)
        : normalizeEmailDesign(cloneScratchValue(emailDesign));
      const nextName = savedTemplate?.name || templateName;
      const nextSubject = savedTemplate?.subject ?? emailSubject;

      setBlocks(nextBlocks);
      setEmailDesign(nextDesign);
      setTemplateName(nextName);
      setEmailSubject(nextSubject);
      setPendingScratchAssets({});
      setSavedSnapshot(createDesignerSnapshot(nextName, nextSubject, nextBlocks, nextDesign));
      setToastMessage("Scratch template saved");
    } catch (error) {
      console.error("Failed to save scratch template:", error);
      setToastMessage(error?.response?.data?.error || "Failed to save scratch template");
    }
  };

  const handleDiscardChanges = () => {
    if (!savedSnapshot) return;
    setTemplateName(savedSnapshot.name);
    setEmailSubject(savedSnapshot.subject);
    setBlocks(cloneScratchValue(savedSnapshot.blocks));
    setEmailDesign(cloneScratchValue(savedSnapshot.design));
    setSelectedId(null);
    setHistory([]);
    setFuture([]);
    setPendingScratchAssets({});
    setDiscardAlertOpen(false);
    setToastMessage("Changes discarded");
  };

  const handleFocusMissingReview = () => {
    if (firstInvalidReviewId) setSelectedId(firstInvalidReviewId);
    setReviewLinkAlertOpen(false);
  };

  // Open the duplicate dialog, prefilling from the current template
  const handleOpenDuplicate = () => {
    setDuplicateForm({
      name: `Copy of ${templateName}`,
      subject: emailSubject,
      purpose: activeTemplate?.purpose || "welcome",
    });
    setDuplicateDialogOpen(true);
  };

  // Confirm duplication: clone on the backend, then navigate to the matching editor
  const handleConfirmDuplicate = async () => {
    if (!activeTemplateId || !duplicateForm.name.trim()) return;
    try {
      const response = await duplicateTemplateAsync({
        templateId: activeTemplateId,
        data: {
          name: duplicateForm.name.trim(),
          subject: duplicateForm.subject.trim(),
          purpose: duplicateForm.purpose,
        },
      });
      const createdTemplate = response.data;
      setDuplicateDialogOpen(false);
      const editorPath = createdTemplate.template_type === "scratch"
        ? `/marketing/templates/designer/${createdTemplate.template_id}`
        : `/marketing/templates/editor/${createdTemplate.template_id}`;
      navigate(editorPath, { state: { template: createdTemplate } });
    } catch (error) {
      console.error("Failed to duplicate template:", error);
      alert(error?.response?.data?.error || "Failed to duplicate template. Please try again.");
    }
  };

  const handleSendTest = async () => {
    setTestEmailError("");
    if (!activeTemplateId) {
      setTestEmailError("Save the template before sending a test email");
      return;
    }
    if (!testEmailAddress.trim()) {
      setTestEmailError("Please enter an email address");
      return;
    }
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(testEmailAddress)) {
      setTestEmailError("Please enter a valid email address");
      return;
    }

    setIsSendingTest(true);
    try {
      const senderDomainId = testSenderDomainId === "default" ? null : testSenderDomainId;
      await sendTestEmail(activeTemplateId, testEmailAddress.trim(), senderDomainId);
      setSendTestOpen(false);
      setTestEmailAddress("");
      setTestSenderDomainId("default");
      setToastMessage("Test email sent");
    } catch (error) {
      console.error("Failed to send test email:", error);
      setTestEmailError(error?.response?.data?.error || "Failed to send test email");
    } finally {
      setIsSendingTest(false);
    }
  };

  /* ── keyboard shortcuts ── */
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedId && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA" && !document.activeElement?.isContentEditable) {
          e.preventDefault();
          deleteBlock(selectedId);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  /* ═══════════════════════════ RENDER ═══════════════════════════ */
  return (
    <>
      <Flex direction="column" p="5" gap="5" style={{ width: '100%', height: '100%', flex: '1 0 0' }}>
        {/* Drag-and-drop visual styles */}
        <style>{`
          .scratch-drop-indicator { height: 3px; background: var(--accent-9); border-radius: 2px; margin: 0 24px; position: relative; }
          .scratch-drop-indicator::before, .scratch-drop-indicator::after { content:''; position:absolute; top:-3px; width:9px; height:9px; border-radius:50%; background:var(--accent-9); }
          .scratch-drop-indicator::before { left:-4px; } .scratch-drop-indicator::after { right:-4px; }
          .scratch-canvas-block:hover { outline: 2px solid #9ca3af; outline-offset: -2px; border-radius: var(--radius-2); }
          .scratch-canvas-block.selected { outline: 2px solid var(--accent-9); outline-offset: -2px; border-radius: var(--radius-2); }
          .scratch-canvas-block.merge-target { outline: 2px dashed var(--accent-9); outline-offset: -2px; border-radius: var(--radius-2); background: var(--accent-a2); }
          .scratch-canvas-block.dragging { opacity: 0.4; }
          .scratch-block-card { cursor: grab; user-select: none; }
          .scratch-block-card:active { cursor: grabbing; }
          .scratch-grip { cursor: grab; opacity: 0; transition: opacity 0.15s; }
          .scratch-grip:active { cursor: grabbing; }
          .scratch-canvas-block:hover .scratch-grip { opacity: 1; }
          .scratch-col-cell { border: 1.5px dashed #9ca3af; border-radius: var(--radius-2); min-height: 60px; transition: border-color 0.15s, background 0.15s; }
          .scratch-col-cell.idle { border-color: transparent; }
          .scratch-col-cell:hover { border-color: #9ca3af; }
          .scratch-col-cell.drag-over { border-color: var(--accent-9); background: var(--accent-a2); }
        `}</style>

        {/* ═══ HEADER ═══ */}
        <Flex align="center" justify="between" style={{ alignItems: "center", alignSelf: "stretch"  }}>
          <Flex align="center" gap="3" style={{ minWidth: 0, flex: 1 }}>
            <Tooltip content="Back to templates">
              <IconButton variant="soft" color="gray" size="2" onClick={() => navigate("/marketing/templates")}>
                <ArrowLeftIcon />
              </IconButton>
            </Tooltip>
            <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
              <Flex align="center" gap="2" wrap="wrap">
                {editingName ? (
                  <TextField.Root
                    autoFocus
                    size="2"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    onBlur={() => setEditingName(false)}
                    onKeyDown={(e) => e.key === "Enter" && setEditingName(false)}
                    style={{ width: 240 }}
                  />
                ) : (
                  <Text
                    size="3"
                    weight="bold"
                    onClick={() => setEditingName(true)}
                    style={{ cursor: "pointer", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {templateName}
                  </Text>
                )}
                <Badge color="orange" variant="soft" size="1">Scratch</Badge>
              </Flex>
              <Text size="1" color="gray" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                Subject: {emailSubject || "No subject line"}
              </Text>
            </Flex>
          </Flex>

          <Flex align="center" gap="2" wrap="wrap" justify="end">
            <Button size="2" variant="soft" color="gray"
              onClick={handleOpenDuplicate}
              disabled={!activeTemplateId || isDuplicating}>
              <CopyIcon />
              Duplicate template
            </Button>

            <Button size="2" variant="soft" color="amber" 
              onClick={() => { setTestEmailError(""); setSendTestOpen(true); }} 
              disabled={!activeTemplateId}>
              <EnvelopeOpenIcon />
              Send test email
            </Button>

            <Tooltip content={`Switch to ${viewMode === "mobile" ? "desktop" : "mobile"} preview`} side="bottom">
              <IconButton size="2" variant="soft" color="gray" onClick={() => setViewMode(viewMode === "mobile" ? "desktop" : "mobile")}>
                {viewMode === "mobile" ? <ComputerDesktopIcon width={16} height={16} /> : <DevicePhoneMobileIcon width={16} height={16} />}
              </IconButton>
            </Tooltip>
          </Flex>
        </Flex>

        {/* ═══ BODY: left panel + canvas + right properties ═══ */}
        <Flex gap="5" style={{ flex: '1 0 0', overflow: "hidden" }}>

          {/* ─── LEFT SIDEBAR ─── */}
          <Flex style={{ alignItems: "flex-start", alignSelf: "stretch", overflow: "hidden" }} height="100%">
            <Flex
              width="300px"
              direction="column"
              style={{
                alignItems: "flex-start",
                alignSelf: "stretch",
                flex: "1 0 0",
                borderRadius: "var(--radius-4)",
                height: "100%",
                border: "1px solid var(--gray-a6)",
                backgroundColor: "var(--color-panel-solid)",
                overflow: "hidden",
              }}
            >
              <Flex direction="column" style={{ alignSelf: "stretch", overflow: "scroll", flex: "1 0 0" }}>
                <Tabs.Root value={leftTab} onValueChange={setLeftTab}>
                  <Flex direction="column" style={{ position: "sticky", top: "0", zIndex: "2", backgroundColor: "var(--color-panel-solid)", overflow: "hidden" }}>
                    <Tabs.List style={{ padding: "var(--space-2) var(--space-4) 0px var(--space-4)", borderTopLeftRadius: "var(--radius-4)", borderTopRightRadius: "var(--radius-4)" }}>
                      <Tabs.Trigger value="blocks">Blocks</Tabs.Trigger>
                      <Tabs.Trigger value="design">Design</Tabs.Trigger>
                    </Tabs.List>
                  </Flex>

                  <Box style={{ flex: 1, overflow: "auto", padding: "var(--space-3)" }}>
                {/* ── Blocks tab ── */}
                <Tabs.Content value="blocks">
                  <Flex direction="column" gap="3">
                    <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                      Content Blocks
                    </Text>
                    <Text size="1" color="gray">Drag or click to add to your email.</Text>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      {CONTENT_BLOCKS.map((b) => (
                        <div
                          key={b.type}
                          draggable
                          onDragStart={(e) => handleSidebarDragStart(e, b.type)}
                          onDragEnd={handleDragEnd}
                          className="scratch-block-card"
                        >
                          <SidebarBlockCard type={b.type} label={b.label} onClick={() => addBlock(b.type)} />
                        </div>
                      ))}
                    </div>

                    <Separator size="4" />

                    <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                      Layout
                    </Text>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      {LAYOUT_BLOCKS.map((b) => (
                        <div
                          key={b.label}
                          draggable
                          onDragStart={(e) => {
                            setDragType("new");
                            setDragNewBlockType(`columns:${b.colCount}`);
                            e.dataTransfer.setData("text/plain", `new:columns:${b.colCount}`);
                            e.dataTransfer.effectAllowed = "copy";
                          }}
                          onDragEnd={handleDragEnd}
                          className="scratch-block-card"
                        >
                          <SidebarBlockCard
                            type="columns"
                            label={b.label}
                            onClick={() => {
                              pushHistory(blocks);
                              const cb = createColumnsBlock(b.colCount);
                              setBlocks([...blocks, cb]);
                              setSelectedId(cb.id);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </Flex>
                </Tabs.Content>

                {/* ── Design tab ── */}
                <Tabs.Content value="design">
                  <Flex direction="column" gap="4">
                    <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                      Email details
                    </Text>
                    <PropField label="Subject line">
                      <TextField.Root
                        placeholder="Email's subject line"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                      />
                    </PropField>

                    <Separator size="4" />

                    <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                      Email Background
                    </Text>
                    <PropField label="Content background">
                      <ColorInput value={emailDesign.contentBgColor} onChange={(v) => updateDesign({ contentBgColor: v })} />
                    </PropField>

                    <Separator size="4" />

                    <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                      Layout
                    </Text>
                    <PropField label="Content width (px)">
                      <Flex align="center" gap="2">
                        <input
                          type="range" min="400" max="600" step="10"
                          value={emailDesign.contentWidth}
                          onChange={(e) => updateDesign({ contentWidth: e.target.value })}
                          style={{ flex: 1, accentColor: "var(--accent-9)" }}
                        />
                        <Text size="1" color="gray" style={{ minWidth: 40, textAlign: "right" }}>{emailDesign.contentWidth}px</Text>
                      </Flex>
                    </PropField>
                    <PropField label="Content padding (px)">
                      <Flex align="center" gap="2">
                        <input
                          type="range" min="0" max="40" step="4"
                          value={emailDesign.contentPadding}
                          onChange={(e) => updateDesign({ contentPadding: e.target.value })}
                          style={{ flex: 1, accentColor: "var(--accent-9)" }}
                        />
                        <Text size="1" color="gray" style={{ minWidth: 40, textAlign: "right" }}>{emailDesign.contentPadding}px</Text>
                      </Flex>
                    </PropField>
                    <PropField label="Border radius (px)">
                      <Flex align="center" gap="2">
                        <input
                          type="range" min="0" max="24" step="2"
                          value={emailDesign.contentBorderRadius}
                          onChange={(e) => updateDesign({ contentBorderRadius: e.target.value })}
                          style={{ flex: 1, accentColor: "var(--accent-9)" }}
                        />
                        <Text size="1" color="gray" style={{ minWidth: 40, textAlign: "right" }}>{emailDesign.contentBorderRadius}px</Text>
                      </Flex>
                    </PropField>

                    <Separator size="4" />

                    <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                      Typography
                    </Text>
                    <PropField label="Global font">
                      <select
                        value={emailDesign.globalFontFamily}
                        onChange={(e) => updateDesign({ globalFontFamily: e.target.value })}
                        style={{ width: "100%", padding: "6px 8px", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)", fontSize: 13, fontFamily: "inherit", background: "var(--color-background)" }}
                      >
                        {["Arial, sans-serif","Georgia, serif","Verdana, sans-serif","Helvetica, sans-serif","'Courier New', monospace","'Trebuchet MS', sans-serif","Tahoma, sans-serif","'Times New Roman', serif"].map((f) => (
                          <option key={f} value={f}>{f.split(",")[0].replace(/'/g, "")}</option>
                        ))}
                      </select>
                    </PropField>

                    <Separator size="4" />

                    <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                      Footer
                    </Text>
                    <Text size="1" color="gray">
                      Keep the required browser, preferences, and copyright lines, then add custom text lines and move them into the order you want.
                    </Text>
                    <PropField label="Footer background">
                      <ColorInput value={emailDesign.footerBgColor || DEFAULT_EMAIL_DESIGN.footerBgColor} onChange={(v) => updateDesign({ footerBgColor: v })} />
                    </PropField>
                    <PropField label="Text colour">
                      <ColorInput value={emailDesign.footerTextColor || DEFAULT_EMAIL_DESIGN.footerTextColor} onChange={(v) => updateDesign({ footerTextColor: v })} />
                    </PropField>
                    <PropField label="Link colour">
                      <ColorInput value={emailDesign.footerLinkColor || DEFAULT_EMAIL_DESIGN.footerLinkColor} onChange={(v) => updateDesign({ footerLinkColor: v })} />
                    </PropField>
                    <PropField label="Font size (px)">
                      <Flex align="center" gap="2">
                        <input
                          type="range" min="10" max="18" step="1"
                          value={getFooterFontSize(emailDesign)}
                          onChange={(e) => updateDesign({ footerFontSize: e.target.value })}
                          style={{ flex: 1, accentColor: "var(--accent-9)" }}
                        />
                        <Text size="1" color="gray" style={{ minWidth: 40, textAlign: "right" }}>
                          {getFooterFontSize(emailDesign)}px
                        </Text>
                      </Flex>
                    </PropField>
                    <Button variant="soft" color="gray" size="2" onClick={addFooterTextLine}>
                      <PlusIcon />
                      Add footer text line
                    </Button>
                    <Flex direction="column" gap="2">
                      {footerLines.map((line, index) => (
                        <Box key={line.id} style={{ border: "1px solid var(--gray-a6)", borderRadius: "var(--radius-3)", padding: 12 }}>
                          <Flex align="center" justify="between" gap="2" mb="2">
                            <Text size="1" weight="bold">
                              {line.type === "browser" ? "Browser view line" : line.type === "preferences" ? "Preferences line" : line.type === "copyright" ? "Copyright line" : "Custom text line"}
                            </Text>
                            <Flex gap="1">
                              <IconButton variant="soft" color="gray" size="1" disabled={index === 0} onClick={() => moveFooterLine(line.id, -1)}>
                                <ChevronUpIcon />
                              </IconButton>
                              <IconButton variant="soft" color="gray" size="1" disabled={index === footerLines.length - 1} onClick={() => moveFooterLine(line.id, 1)}>
                                <ChevronDownIcon />
                              </IconButton>
                              {line.type === "custom" ? (
                                <IconButton variant="soft" color="red" size="1" onClick={() => removeFooterLine(line.id)}>
                                  <TrashIcon />
                                </IconButton>
                              ) : null}
                            </Flex>
                          </Flex>

                          {line.type === "browser" ? (
                            <>
                              <Text size="1" color="gray" style={{ display: "block", marginBottom: 6 }}>
                                Browser-view link text
                              </Text>
                              <TextField.Root
                                placeholder="view email in browser"
                                value={line.linkText || ""}
                                onChange={(e) => updateFooterLine(line.id, { linkText: e.target.value })}
                              />
                            </>
                          ) : line.type === "preferences" ? (
                            <>
                              <Text size="1" color="gray" style={{ display: "block", marginBottom: 6 }}>
                                Optional line above the required update and unsubscribe links
                              </Text>
                              <TextArea
                                placeholder="Want to change how you receive communications?"
                                value={line.introText || ""}
                                onChange={(e) => updateFooterLine(line.id, { introText: e.target.value })}
                              />
                            </>
                          ) : (
                            <>
                              <Text size="1" color="gray" style={{ display: "block", marginBottom: 6 }}>
                                {line.type === "copyright" ? "Supports {year} and {brandName}." : "Add any extra footer copy here."}
                              </Text>
                              <TextArea
                                placeholder={line.type === "copyright" ? "© {year} {brandName}. All rights reserved." : "123 Main Street\nCity, State 12345"}
                                value={line.text || ""}
                                onChange={(e) => updateFooterLine(line.id, { text: e.target.value })}
                              />
                            </>
                          )}
                        </Box>
                      ))}
                    </Flex>
                  </Flex>
                </Tabs.Content>

                  </Box>
                </Tabs.Root>
              </Flex>

              {/* Save / Discard */}
              <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
              <Flex p="3" gap="2" style={{ alignSelf: "stretch" }}>
                <Button
                  variant="soft"
                  color="red"
                  size="2"
                  onClick={() => setDiscardAlertOpen(true)}
                  disabled={!hasUnsavedChanges}
                  style={{ flex: 1 }}
                >
                  Discard changes
                </Button>
                <Button
                  variant="solid"
                  size="2"
                  onClick={handleSave}
                  loading={isUpdating}
                  disabled={!blocks.length || !emailSubject.trim() || !hasUnsavedChanges}
                  style={{ flex: 1 }}
                >
                  Save changes
                </Button>
              </Flex>
            </Flex>
          </Flex>

          {/* ─── CANVAS ─── */}
          <Flex
            direction="column"
            onClick={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}
            onDragOver={handleCanvasDragOver}
            onDragLeave={handleCanvasDragLeave}
            onDrop={handleCanvasDrop}
            style={{
              flex: "1 0 0",
              alignItems: "center",
              alignSelf: "stretch",
              overflow: "auto",
              background: "var(--gray-a3)",
              display: "flex",
              padding: "24px 20px",
              transition: "background 0.3s",
              borderRadius: "var(--radius-4)",
              border: "1px solid var(--gray-a6)",
            }}
          >
            <div
              style={{
                width: viewMode === "mobile" ? 375 : Math.min(parseInt(emailDesign.contentWidth) || 600, 600),
                transition: "width 0.3s",
                background: emailDesign.bgColor,
                borderRadius: viewMode === "mobile" ? "10px" : `${emailDesign.contentBorderRadius}px`,
              }}
            >
              {viewMode === "mobile" && (
                <div style={{ background: "var(--gray-12)", padding: "6px 0", textAlign: "center", fontSize: 10, color: "var(--gray-8)", fontWeight: 600, letterSpacing: 0.5, borderRadius: "10px 10px 0 0" }}>
                  MOBILE PREVIEW — 375px
                </div>
              )}

              <div
                ref={canvasRef}
                onClick={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}
                style={{
                  background: emailDesign.contentBgColor,
                  fontFamily: emailDesign.globalFontFamily,
                  boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
                  borderRadius: viewMode === "mobile" ? "0 0 10px 10px" : `${emailDesign.contentBorderRadius}px`,
                  border: `1px solid ${isDraggingOver ? "var(--accent-9)" : "var(--gray-a5)"}`,
                  minHeight: "auto",
                  overflow: "visible",
                  transition: "border-color 0.15s, background 0.3s",
                  padding: `${emailDesign.contentPadding}px`,
                }}
              >
                {blocks.length === 0 ? (
                  <Flex direction="column" align="center" justify="center" style={{ padding: isDraggingOver ? 24 : "80px 30px", textAlign: "center" }}>
                    {isDraggingOver ? (
                      <Box style={{ border: "2px dashed var(--accent-9)", background: "var(--accent-a2)", borderRadius: "var(--radius-3)", padding: "48px 24px", width: "100%" }}>
                        <Text size="3" weight="bold" color="indigo">Drop here to add</Text>
                      </Box>
                    ) : (
                      <>
                        <Text size="7" style={{ marginBottom: 12, opacity: 0.25 }}>✉️</Text>
                        <Text size="3" weight="bold" style={{ marginBottom: 6 }}>Start building your email</Text>
                        <Text size="2" color="gray">Drag blocks from the left panel, or click them to add.</Text>
                      </>
                    )}
                  </Flex>
                ) : (
                  <div>
                    {blocks.map((block, idx) => (
                      <div key={block.id}>
                        {dropIndex === idx && isDraggingOver && <div className="scratch-drop-indicator" />}
                        <CanvasBlock
                          block={block}
                          idx={idx}
                          isSelected={selectedId === block.id}
                          selectedId={selectedId}
                          isDragging={dragBlockId === block.id}
                          onSelect={() => setSelectedId(block.id)}
                          onSelectChild={(childId) => setSelectedId(childId)}
                          onUpdate={(u) => updateBlock(block.id, u)}
                          onDelete={() => deleteBlock(block.id)}
                          onDuplicate={() => duplicateBlock(block.id)}
                          onMoveUp={() => moveBlock(block.id, -1)}
                          onMoveDown={() => moveBlock(block.id, 1)}
                          onDragStart={(e) => handleCanvasDragStart(e, block.id, block.type)}
                          onDragEnd={handleDragEnd}
                          isFirst={idx === 0}
                          isLast={idx === blocks.length - 1}
                          isMergeTarget={mergeTargetBlockId === block.id}
                          addBlockToColumn={addBlockToColumn}
                          updateColumnBlock={updateColumnBlock}
                          deleteColumnBlock={deleteColumnBlock}
                          moveColumnBlock={moveColumnBlock}
                          dropIntoColumn={dropIntoColumn}
                          dragType={dragType}
                          dragNewBlockType={dragNewBlockType}
                          moveDraggedMediaBlockToColumn={moveDraggedMediaBlockToColumn}
                          onColumnMediaDragStart={handleColumnMediaDragStart}
                          pushHistory={pushHistory}
                          allBlocks={blocks}
                          viewMode={viewMode}
                          handleScratchFileSelection={handleScratchFileSelection}
                        />
                      </div>
                    ))}
                    {dropIndex === blocks.length && isDraggingOver && <div className="scratch-drop-indicator" />}
                  </div>
                )}

                {/* Footer */}
                <EmailFooter brandName={userProfile?.organisation_name || templateName || "Your Brand"} design={emailDesign} />
              </div>
            </div>
          </Flex>

          {/* ─── RIGHT PROPERTIES PANEL ─── */}
          {selected && (
            <Flex style={{ alignItems: "flex-start", alignSelf: "stretch", overflow: "hidden" }} height="100%">
            <Flex
              width="300px"
              direction="column"
              style={{
                alignItems: "flex-start",
                alignSelf: "stretch",
                flex: "1 0 0",
                borderRadius: "var(--radius-4)",
                height: "100%",
                border: "1px solid var(--gray-a6)",
                backgroundColor: "var(--color-panel-solid)",
                overflow: "auto",
              }}
            >
              <Flex direction="column" gap="3" p="4" style={{ width: "100%", boxSizing: "border-box", minWidth: 0 }}>
                <Text size="3" weight="bold" style={{ textTransform: "capitalize" }}>
                  {selected.type} Block
                </Text>
                <Separator size="4" />

                {/* ── Section settings (all blocks) ── */}
                <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>Section</Text>
                <PropField label="Background colour">
                  <ColorInput
                    value={selected.sectionBg === "transparent" ? "#ffffff" : (selected.sectionBg || "#ffffff")}
                    onChange={(v) => updateBlock(selected.id, { sectionBg: v })}
                  />
                  <Flex gap="1" mt="1">
                    {[
                      { l: "None", v: "transparent" },
                      { l: "Light", v: "#F9FAFB" },
                      { l: "Dark", v: "#111827" },
                    ].map((p) => (
                      <Button
                        key={p.l}
                        variant={(selected.sectionBg || "transparent") === p.v ? "solid" : "outline"}
                        color="gray"
                        size="1"
                        style={{ flex: 1 }}
                        onClick={() => updateBlock(selected.id, { sectionBg: p.v })}
                      >
                        {p.l}
                      </Button>
                    ))}
                  </Flex>
                </PropField>
                <PropField label="Section padding">
                  <Flex align="center" gap="2">
                    <input
                      type="range" min="0" max="60" step="4"
                      value={parseInt(selected.sectionPadding) || 0}
                      onChange={(e) => updateBlock(selected.id, { sectionPadding: e.target.value })}
                      style={{ flex: 1, accentColor: "var(--accent-9)" }}
                    />
                    <Text size="1" color="gray" style={{ minWidth: 30, textAlign: "right" }}>{selected.sectionPadding || "0"}px</Text>
                  </Flex>
                </PropField>

                <Separator size="4" />

                {/* ── Type‐specific properties ── */}

                {/* HEADING / PARAGRAPH */}
                {(selected.type === "heading" || selected.type === "paragraph") && (
                  <>
                    <PropField label="Text">
                      <TextArea
                        value={selected.text}
                        onChange={(e) => updateBlock(selected.id, { text: e.target.value })}
                        rows={3}
                        resize="vertical"
                      />
                    </PropField>
                    <PropField label="Font family">
                      <select
                        value={selected.fontFamily || ""}
                        onChange={(e) => updateBlock(selected.id, { fontFamily: e.target.value })}
                        style={{ width: "100%", padding: "6px 8px", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)", fontSize: 13, fontFamily: "inherit", background: "var(--color-background)" }}
                      >
                        <option value="">Use global font</option>
                        {["Arial, sans-serif","Georgia, serif","Verdana, sans-serif","Helvetica, sans-serif","'Courier New', monospace"].map((f) => (
                          <option key={f} value={f}>{f.split(",")[0].replace(/'/g, "")}</option>
                        ))}
                      </select>
                    </PropField>
                    <PropField label="Font size">
                      <Flex align="center" gap="2">
                        <input type="range" min="1" max="200" step="1" value={parseInt(selected.fontSize) || 16} onChange={(e) => updateBlock(selected.id, { fontSize: e.target.value + "px" })} style={{ flex: 1, accentColor: "var(--accent-9)" }} />
                        <TextField.Root size="1" style={{ width: 52 }} value={selected.fontSize ? String(parseInt(selected.fontSize) || "") : ""} onChange={(e) => { const v = e.target.value; if (v === "" || /^\d+$/.test(v)) updateBlock(selected.id, { fontSize: v ? `${v}px` : "" }); }} />
                      </Flex>
                    </PropField>
                    <PropField label="Line height">
                      <TextField.Root
                        step="0.1"
                        value={selected.lineHeight || ""}
                        placeholder="1.6"
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "" || /^\d*\.?\d*$/.test(v)) updateBlock(selected.id, { lineHeight: v });
                        }}
                      />
                    </PropField>
                    <PropField label="Alignment">
                      <Flex gap="1">
                        {["left", "center", "right"].map((a) => (
                          <Button key={a} variant={(selected.textAlign || "left") === a ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1, textTransform: "capitalize" }} onClick={() => updateBlock(selected.id, { textAlign: a })}>
                            {a}
                          </Button>
                        ))}
                      </Flex>
                    </PropField>
                    <PropField label="Font weight">
                      <Flex gap="1">
                        {[["400","Normal"],["700","Bold"]].map(([v,l]) => (
                          <Button key={v} variant={(selected.fontWeight || "400") === v ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1 }} onClick={() => updateBlock(selected.id, { fontWeight: v })}>
                            {l}
                          </Button>
                        ))}
                      </Flex>
                    </PropField>
                    <PropField label="Colour">
                      <ColorInput value={selected.color || "#111827"} onChange={(v) => updateBlock(selected.id, { color: v })} />
                    </PropField>
                  </>
                )}

                {/* IMAGE */}
                {selected.type === "image" && (
                  <>
                    <PropField label="Image">
                      <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 12px", borderRadius: "var(--radius-2)", border: "1px solid var(--accent-a6)", background: "var(--accent-a2)", color: "var(--accent-11)", fontSize: 13, fontWeight: 600, cursor: "pointer", width: "100%", boxSizing: "border-box" }}>
                        Upload image
                        <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                          const f = e.target.files?.[0];
                          handleScratchFileSelection(f, (previewSrc, assetKey) => updateBlock(selected.id, { src: previewSrc, srcAssetKey: assetKey }), selected.srcAssetKey);
                          e.target.value = "";
                        }} />
                      </label>
                    </PropField>
                    <PropField label="Image source URL">
                      <TextField.Root
                        placeholder="https://example.com/image.jpg"
                        value={selected.src?.startsWith("data:") ? "" : selected.src || ""}
                        onChange={(e) => {
                          clearPendingScratchAsset(selected.srcAssetKey);
                          updateBlock(selected.id, { src: e.target.value, srcAssetKey: "" });
                        }}
                      />
                    </PropField>
                    <PropField label="Click-through URL">
                      <TextField.Root
                        placeholder="https://example.com"
                        value={selected.linkUrl || ""}
                        onChange={(e) => updateBlock(selected.id, { linkUrl: e.target.value })}
                        onBlur={(e) => { const n = ensureAbsoluteUrl(e.target.value); if (n !== e.target.value) updateBlock(selected.id, { linkUrl: n }); }}
                      />
                    </PropField>
                    {selected.src && (
                      <img src={selected.src} alt="" style={{ width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a5)", marginBottom: 8 }} />
                    )}
                    <PropField label="Size">
                      <Flex align="center" gap="2">
                        <input type="range" min="1" max="2000" step="5" value={getImageSizeNumber(selected)} onChange={(e) => updateBlock(selected.id, { imageSize: e.target.value, width: "", height: "" })} style={{ flex: 1, accentColor: "var(--accent-9)" }} />
                        <TextField.Root size="1" style={{ width: 58 }} value={selected.imageSize ?? ""} onChange={(e) => { const v = e.target.value; if (v === "" || /^\d+$/.test(v)) updateBlock(selected.id, { imageSize: v, width: "", height: "" }); }} />
                      </Flex>
                    </PropField>
                    <PropField label="Alignment">
                      <Flex gap="1">
                        {["left","center","right"].map((a) => (
                          <Button key={a} variant={(selected.align || "center") === a ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1, textTransform: "capitalize" }} onClick={() => updateBlock(selected.id, { align: a })}>
                            {a}
                          </Button>
                        ))}
                      </Flex>
                    </PropField>
                    <PropField label="Border radius">
                      <Flex align="center" gap="2">
                        <input type="range" min="0" max="24" step="2" value={parseInt(selected.borderRadius) || 0} onChange={(e) => updateBlock(selected.id, { borderRadius: e.target.value + "px" })} style={{ flex: 1, accentColor: "var(--accent-9)" }} />
                        <Text size="1" color="gray" style={{ minWidth: 40, textAlign: "right" }}>{selected.borderRadius || "0px"}</Text>
                      </Flex>
                    </PropField>
                    <PropField label="Alt text">
                      <TextField.Root value={selected.alt || ""} onChange={(e) => updateBlock(selected.id, { alt: e.target.value })} />
                    </PropField>
                  </>
                )}

                {/* BUTTON */}
                {selected.type === "button" && (
                  <>
                    <PropField label="Button text">
                      <TextField.Root value={selected.text || ""} onChange={(e) => updateBlock(selected.id, { text: e.target.value })} />
                    </PropField>
                    <PropField label="URL">
                      <TextField.Root placeholder="https://..." value={selected.url || ""} onChange={(e) => updateBlock(selected.id, { url: e.target.value })} />
                    </PropField>
                    <PropField label="Background colour">
                      <ColorInput value={selected.bgColor || "#4F46E5"} onChange={(v) => updateBlock(selected.id, { bgColor: v })} />
                    </PropField>
                    <PropField label="Text colour">
                      <ColorInput value={selected.textColor || "#ffffff"} onChange={(v) => updateBlock(selected.id, { textColor: v })} />
                    </PropField>
                    <PropField label="Width">
                      <Flex gap="1">
                        <Button variant={!selected.fullWidth ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1 }} onClick={() => updateBlock(selected.id, { fullWidth: false })}>Auto</Button>
                        <Button variant={selected.fullWidth ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1 }} onClick={() => updateBlock(selected.id, { fullWidth: true })}>Full width</Button>
                      </Flex>
                    </PropField>
                    <PropField label="Padding">
                      <Flex gap="1">
                        {[{l:"S",v:"8px 20px"},{l:"M",v:"14px 32px"},{l:"L",v:"18px 44px"}].map((p) => (
                          <Button key={p.l} variant={selected.padding === p.v ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1 }} onClick={() => updateBlock(selected.id, { padding: p.v })}>
                            {p.l}
                          </Button>
                        ))}
                      </Flex>
                    </PropField>
                    <PropField label="Font size">
                      <Flex align="center" gap="2">
                        <input type="range" min="1" max="200" step="1" value={parseInt(selected.fontSize) || 16} onChange={(e) => updateBlock(selected.id, { fontSize: e.target.value + "px" })} style={{ flex: 1, accentColor: "var(--accent-9)" }} />
                        <TextField.Root size="1" style={{ width: 52 }} value={selected.fontSize ? String(parseInt(selected.fontSize) || "") : ""} onChange={(e) => { const v = e.target.value; if (v === "" || /^\d+$/.test(v)) updateBlock(selected.id, { fontSize: v ? `${v}px` : "" }); }} />
                      </Flex>
                    </PropField>
                    <PropField label="Border radius">
                      <Flex gap="1">
                        {[{l:"None",v:"0px"},{l:"Soft",v:"6px"},{l:"Round",v:"24px"},{l:"Pill",v:"50px"}].map((r) => (
                          <Button key={r.l} variant={(selected.borderRadius || "6px") === r.v ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1 }} onClick={() => updateBlock(selected.id, { borderRadius: r.v })}>
                            {r.l}
                          </Button>
                        ))}
                      </Flex>
                    </PropField>
                    <PropField label="Alignment">
                      <Flex gap="1">
                        {["left","center","right"].map((a) => (
                          <Button key={a} variant={(selected.align || "center") === a ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1, textTransform: "capitalize" }} onClick={() => updateBlock(selected.id, { align: a })}>
                            {a}
                          </Button>
                        ))}
                      </Flex>
                    </PropField>
                  </>
                )}

                {/* REVIEW */}
                {selected.type === "review" && (
                  <>
                    <PropField label="Eyebrow label">
                      <TextField.Root value={selected.badgeText || ""} onChange={(e) => updateBlock(selected.id, { badgeText: e.target.value })} />
                    </PropField>
                    <PropField label="Heading">
                      <TextArea value={selected.heading || ""} onChange={(e) => updateBlock(selected.id, { heading: e.target.value })} />
                    </PropField>
                    <PropField label="Supporting text">
                      <TextArea value={selected.helperText || ""} onChange={(e) => updateBlock(selected.id, { helperText: e.target.value })} />
                    </PropField>
                    <PropField label="Review URL">
                      <TextField.Root placeholder="https://example.com/review" value={selected.url || ""} onChange={(e) => updateBlock(selected.id, { url: e.target.value })} onBlur={(e) => { const n = ensureAbsoluteUrl(e.target.value); if (n !== e.target.value) updateBlock(selected.id, { url: n }); }} />
                    </PropField>
                    <PropField label="Survey template">
                      <Select.Root
                        value={selected.surveyTemplateId || NO_SURVEY_TEMPLATE_VALUE}
                        onValueChange={(value) => {
                          if (value === NO_SURVEY_TEMPLATE_VALUE) {
                            updateBlock(selected.id, { surveyTemplateId: "", surveyUrl: "" });
                            return;
                          }
                          updateBlock(selected.id, {
                            surveyTemplateId: value,
                            surveyUrl: getPublicSurveyEmailLink(value),
                          });
                        }}
                      >
                        <Select.Trigger
                          placeholder={isLoadingSurveyTemplates ? "Loading surveys..." : "Select a survey"}
                          style={{ width: "100%" }}
                        />
                        <Select.Content>
                          <Select.Item value={NO_SURVEY_TEMPLATE_VALUE}>
                            No survey selected
                          </Select.Item>
                          {activeSurveyTemplates.length === 0 ? (
                            <Select.Item value="__no_active_survey_templates__" disabled>
                              No active survey templates
                            </Select.Item>
                          ) : activeSurveyTemplates.map((template) => (
                            <Select.Item key={template.template_id} value={template.template_id}>
                              {template.name || "Untitled survey"}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    </PropField>
                    <PropField label="Click-through stars">
                      <Select.Root
                        value={String(getReviewClickThroughMinRating(selected))}
                        onValueChange={(value) => updateBlock(selected.id, { clickThroughMinRating: Number(value) })}
                      >
                        <Select.Trigger style={{ width: "100%" }} />
                        <Select.Content>
                          {REVIEW_CLICK_THROUGH_MIN_RATING_OPTIONS.map((option) => (
                            <Select.Item key={option.value} value={option.value}>
                              {option.label}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    </PropField>
                    <PropField label="Card background">
                      <ColorInput value={selected.cardBgColor || "#FFF8E7"} onChange={(v) => updateBlock(selected.id, { cardBgColor: v })} />
                    </PropField>
                    <PropField label="Heading colour">
                      <ColorInput value={selected.headingColor || "#1F2937"} onChange={(v) => updateBlock(selected.id, { headingColor: v })} />
                    </PropField>
                    <PropField label="Supporting colour">
                      <ColorInput value={selected.helperColor || "#6B7280"} onChange={(v) => updateBlock(selected.id, { helperColor: v })} />
                    </PropField>
                    <PropField label="Star colour">
                      <ColorInput value={selected.accentColor || "#F59E0B"} onChange={(v) => updateBlock(selected.id, { accentColor: v })} />
                    </PropField>
                    <PropField label="Badge text colour">
                      <ColorInput value={selected.badgeTextColor || selected.accentColor || "#F59E0B"} onChange={(v) => updateBlock(selected.id, { badgeTextColor: v })} />
                    </PropField>
                    <PropField label="Star size">
                      <Flex align="center" gap="2">
                        <input type="range" min="16" max="64" step="2" value={getReviewStarSize(selected)} onChange={(e) => updateBlock(selected.id, { starSize: e.target.value })} style={{ flex: 1, accentColor: "var(--accent-9)" }} />
                        <Text size="1" color="gray" style={{ minWidth: 44, textAlign: "right" }}>{`${getReviewStarSize(selected)}px`}</Text>
                      </Flex>
                    </PropField>
                    <PropField label="Border colour">
                      <ColorInput value={selected.borderColor || "#F3D19C"} onChange={(v) => updateBlock(selected.id, { borderColor: v })} />
                    </PropField>
                    <PropField label="Corner radius">
                      <Flex align="center" gap="2">
                        <input type="range" min="0" max="32" step="2" value={parseInt(selected.cardRadius) || 18} onChange={(e) => updateBlock(selected.id, { cardRadius: `${e.target.value}px` })} style={{ flex: 1, accentColor: "var(--accent-9)" }} />
                        <Text size="1" color="gray" style={{ minWidth: 40, textAlign: "right" }}>{selected.cardRadius || "18px"}</Text>
                      </Flex>
                    </PropField>
                    <PropField label="Alignment">
                      <Flex gap="1">
                        {["left","center","right"].map((a) => (
                          <Button key={a} variant={(selected.textAlign || "center") === a ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1, textTransform: "capitalize" }} onClick={() => updateBlock(selected.id, { textAlign: a })}>
                            {a}
                          </Button>
                        ))}
                      </Flex>
                    </PropField>
                  </>
                )}

                {/* DIVIDER */}
                {selected.type === "divider" && (
                  <>
                    <PropField label="Colour">
                      <ColorInput value={selected.color || "#E5E7EB"} onChange={(v) => updateBlock(selected.id, { color: v })} />
                    </PropField>
                    <PropField label="Thickness">
                      <TextField.Root value={selected.thickness || "1px"} onChange={(e) => updateBlock(selected.id, { thickness: e.target.value })} />
                    </PropField>
                    <PropField label="Style">
                      <Flex gap="1">
                        {["solid","dashed","dotted"].map((s) => (
                          <Button key={s} variant={(selected.style || "solid") === s ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1, textTransform: "capitalize" }} onClick={() => updateBlock(selected.id, { style: s })}>
                            {s}
                          </Button>
                        ))}
                      </Flex>
                    </PropField>
                  </>
                )}

                {/* SPACER */}
                {selected.type === "spacer" && (
                  <PropField label="Height">
                    <TextField.Root value={selected.spacerHeight || "40px"} onChange={(e) => updateBlock(selected.id, { spacerHeight: e.target.value })} />
                  </PropField>
                )}

                {/* SOCIAL */}
                {selected.type === "social" && (
                  <>
                    {/* ── Style ── */}
                    <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>Style</Text>
                    <PropField label="Icon size">
                      <Flex align="center" gap="2">
                        <input type="range" min="16" max="48" step="2" value={parseInt(selected.iconSize) || 32} onChange={(e) => updateBlock(selected.id, { iconSize: e.target.value })} style={{ flex: 1, accentColor: "var(--accent-9)" }} />
                        <Text size="1" color="gray" style={{ minWidth: 28, textAlign: "right" }}>{selected.iconSize || "32"}</Text>
                      </Flex>
                    </PropField>
                    <PropField label="Icon colour">
                      <ColorInput value={selected.iconColor || "#111827"} onChange={(v) => updateBlock(selected.id, { iconColor: v })} />
                    </PropField>
                    <PropField label="Text colour">
                      <ColorInput value={selected.textColor || "#111827"} onChange={(v) => updateBlock(selected.id, { textColor: v })} />
                    </PropField>
                    <PropField label="Spacing">
                      <Flex align="center" gap="2">
                        <input type="range" min="0" max="32" step="2" value={parseInt(selected.iconSpacing) || 12} onChange={(e) => updateBlock(selected.id, { iconSpacing: e.target.value })} style={{ flex: 1, accentColor: "var(--accent-9)" }} />
                        <Text size="1" color="gray" style={{ minWidth: 24, textAlign: "right" }}>{selected.iconSpacing || "12"}</Text>
                      </Flex>
                    </PropField>
                    <PropField label="Alignment">
                      <Flex gap="1">
                        {["left","center","right"].map((a) => (
                          <Button key={a} variant={(selected.align || "center") === a ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1, textTransform: "capitalize" }} onClick={() => updateBlock(selected.id, { align: a })}>
                            {a}
                          </Button>
                        ))}
                      </Flex>
                    </PropField>

                    <Separator size="4" />
                    <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: 0.8 }}>Items</Text>

                    {(selected.items || []).map((item, idx) => {
                      const iconFn = SOCIAL_ICONS[item.platform];
                      return (
                        <Flex key={item.id} direction="column" gap="2" style={{ borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a5)", overflow: "hidden" }}>
                          {/* Item header */}
                          <Flex align="center" gap="2" px="2" py="2" style={{ background: "var(--gray-a2)" }}>
                            {item.type === "icon" && (
                              <span style={{ display: "flex", alignItems: "center", lineHeight: 0 }}>
                                {item.iconMode === "upload" && item.customIconSrc
                                  ? <img src={item.customIconSrc} alt="" style={{ width: 18, height: 18, objectFit: "contain" }} />
                                  : iconFn ? iconFn(18, selected.iconColor || "#111827") : <span style={{ width: 18, height: 18, borderRadius: "50%", background: "var(--gray-8)", display: "inline-block" }} />}
                              </span>
                            )}
                            <Text size="1" weight="medium" style={{ flex: 1, textTransform: "capitalize" }}>
                              {item.type === "spacer" ? "Spacer" : item.type === "text" ? (item.label || "Text link") : (item.platform || "Icon")}
                            </Text>
                            <Flex gap="0">
                              {idx > 0 && (
                                <IconButton variant="ghost" color="gray" size="1" onClick={() => {
                                  const items = [...(selected.items || [])]; const temp = items[idx]; items[idx] = items[idx - 1]; items[idx - 1] = temp; updateBlock(selected.id, { items });
                                }}><ChevronUpIcon /></IconButton>
                              )}
                              {idx < (selected.items || []).length - 1 && (
                                <IconButton variant="ghost" color="gray" size="1" onClick={() => {
                                  const items = [...(selected.items || [])]; const temp = items[idx]; items[idx] = items[idx + 1]; items[idx + 1] = temp; updateBlock(selected.id, { items });
                                }}><ChevronDownIcon /></IconButton>
                              )}
                              <IconButton variant="ghost" color="red" size="1" onClick={() => {
                                const items = (selected.items || []).filter((_, i) => i !== idx); updateBlock(selected.id, { items });
                              }}><TrashIcon /></IconButton>
                            </Flex>
                          </Flex>

                          {/* Item body */}
                          <Flex direction="column" gap="2" px="2" pb="2">
                            {item.type === "icon" && (
                              <>
                                {/* Icon source toggle */}
                                <Flex gap="1">
                                  <Button variant={(item.iconMode || "library") === "library" ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1 }} onClick={() => {
                                    const items = [...(selected.items || [])]; items[idx] = { ...items[idx], iconMode: "library" }; updateBlock(selected.id, { items });
                                  }}>Library</Button>
                                  <Button variant={(item.iconMode || "library") === "upload" ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1 }} onClick={() => {
                                    const items = [...(selected.items || [])]; items[idx] = { ...items[idx], iconMode: "upload" }; updateBlock(selected.id, { items });
                                  }}>Custom</Button>
                                </Flex>

                                {/* Platform icons grid (library mode only) */}
                                {(item.iconMode || "library") === "library" && (
                                  <>
                                    <Text size="1" color="gray">Platform</Text>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4 }}>
                                      {["facebook","instagram","twitter","linkedin","youtube","tiktok","pinterest","snapchat","email","custom"].map((p) => {
                                        const pIconFn = SOCIAL_ICONS[p];
                                        const isActive = item.platform === p;
                                        return (
                                          <button
                                            key={p}
                                            title={p.charAt(0).toUpperCase() + p.slice(1)}
                                            onClick={() => {
                                              const items = [...(selected.items || [])];
                                              if (p === "custom") {
                                                items[idx] = { ...items[idx], platform: p, label: "Custom", iconMode: "upload" };
                                              } else {
                                                items[idx] = { ...items[idx], platform: p, label: p.charAt(0).toUpperCase() + p.slice(1) };
                                              }
                                              updateBlock(selected.id, { items });
                                            }}
                                            style={{
                                              all: "unset", boxSizing: "border-box",
                                              display: "flex", alignItems: "center", justifyContent: "center",
                                              padding: 6, borderRadius: "var(--radius-2)",
                                              border: isActive ? "2px solid var(--accent-9)" : "1px solid var(--gray-a4)",
                                              background: isActive ? "var(--accent-a2)" : "var(--color-background)",
                                              cursor: "pointer", transition: "all 0.1s",
                                            }}
                                            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.borderColor = "var(--gray-a6)"; }}
                                            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.borderColor = "var(--gray-a4)"; }}
                                          >
                                            {pIconFn ? pIconFn(16, isActive ? "var(--accent-11)" : "var(--gray-9)") : <span style={{ fontSize: 10 }}>{p[0].toUpperCase()}</span>}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </>
                                )}

                                {/* Custom upload (upload mode only) */}
                                {(item.iconMode || "library") === "upload" && (
                                  <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "6px 10px", borderRadius: "var(--radius-2)", border: "1px solid var(--accent-a6)", background: "var(--accent-a2)", color: "var(--accent-11)", fontSize: 12, fontWeight: 600, cursor: "pointer", width: "100%", boxSizing: "border-box" }}>
                                    {item.customIconSrc ? "Replace icon" : "Upload icon"}
                                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      handleScratchFileSelection(f, (previewSrc, assetKey) => {
                                        const items = [...(selected.items || [])];
                                        items[idx] = { ...items[idx], customIconSrc: previewSrc, customIconAssetKey: assetKey, iconMode: "upload" };
                                        updateBlock(selected.id, { items });
                                      }, item.customIconAssetKey);
                                      e.target.value = "";
                                    }} />
                                  </label>
                                )}

                                <TextField.Root size="1" placeholder="URL" value={item.url || ""} onChange={(e) => {
                                  const items = [...(selected.items || [])]; items[idx] = { ...items[idx], url: e.target.value }; updateBlock(selected.id, { items });
                                }} />
                                <TextField.Root size="1" placeholder="Label / alt text" value={item.label || ""} onChange={(e) => {
                                  const items = [...(selected.items || [])]; items[idx] = { ...items[idx], label: e.target.value }; updateBlock(selected.id, { items });
                                }} />
                              </>
                            )}
                            {item.type === "text" && (
                              <>
                                <TextField.Root size="1" placeholder="Label" value={item.label || ""} onChange={(e) => {
                                  const items = [...(selected.items || [])]; items[idx] = { ...items[idx], label: e.target.value }; updateBlock(selected.id, { items });
                                }} />
                                <TextField.Root size="1" placeholder="URL" value={item.url || ""} onChange={(e) => {
                                  const items = [...(selected.items || [])]; items[idx] = { ...items[idx], url: e.target.value }; updateBlock(selected.id, { items });
                                }} />
                              </>
                            )}
                            {item.type === "spacer" && (
                              <Flex align="center" gap="2">
                                <Text size="1" color="gray">Width</Text>
                                <input type="range" min="4" max="40" step="2" value={parseInt(item.width) || 16} onChange={(e) => {
                                  const items = [...(selected.items || [])]; items[idx] = { ...items[idx], width: e.target.value }; updateBlock(selected.id, { items });
                                }} style={{ flex: 1, accentColor: "var(--accent-9)" }} />
                                <Text size="1" color="gray" style={{ minWidth: 24, textAlign: "right" }}>{item.width || "16"}px</Text>
                              </Flex>
                            )}
                          </Flex>
                        </Flex>
                      );
                    })}

                    <Flex gap="1">
                      <Button variant="outline" color="gray" size="1" style={{ flex: 1 }} onClick={() => {
                        const items = [...(selected.items || []), { id: uid(), type: "icon", platform: "facebook", url: "", label: "Facebook", iconMode: "library", customIconSrc: "" }]; updateBlock(selected.id, { items });
                      }}>
                        <PlusIcon /> Icon
                      </Button>
                      <Button variant="outline" color="gray" size="1" style={{ flex: 1 }} onClick={() => {
                        const items = [...(selected.items || []), { id: uid(), type: "text", label: "Link", url: "" }]; updateBlock(selected.id, { items });
                      }}>
                        <PlusIcon /> Text
                      </Button>
                      <Button variant="outline" color="gray" size="1" style={{ flex: 1 }} onClick={() => {
                        const items = [...(selected.items || []), { id: uid(), type: "spacer", width: "16" }]; updateBlock(selected.id, { items });
                      }}>
                        <PlusIcon /> Spacer
                      </Button>
                    </Flex>
                  </>
                )}

                {/* LOGO */}
                {selected.type === "logo" && (
                  <>
                    <PropField label="Logo image">
                      <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 12px", borderRadius: "var(--radius-2)", border: "1px solid var(--accent-a6)", background: "var(--accent-a2)", color: "var(--accent-11)", fontSize: 13, fontWeight: 600, cursor: "pointer", width: "100%", boxSizing: "border-box" }}>
                        Upload logo
                        <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                          const f = e.target.files?.[0];
                          handleScratchFileSelection(f, (previewSrc, assetKey) => updateBlock(selected.id, { src: previewSrc, srcAssetKey: assetKey }), selected.srcAssetKey);
                          e.target.value = "";
                        }} />
                      </label>
                    </PropField>
                    <PropField label="Click-through URL">
                      <TextField.Root
                        placeholder="https://example.com"
                        value={selected.linkUrl || ""}
                        onChange={(e) => updateBlock(selected.id, { linkUrl: e.target.value })}
                        onBlur={(e) => { const n = ensureAbsoluteUrl(e.target.value); if (n !== e.target.value) updateBlock(selected.id, { linkUrl: n }); }}
                      />
                    </PropField>
                    <PropField label="Size">
                      <Flex align="center" gap="2">
                        <input type="range" min="1" max="2000" step="5" value={parseInt(selected.logoWidth) || 150} onChange={(e) => updateBlock(selected.id, { logoWidth: e.target.value + "px" })} style={{ flex: 1, accentColor: "var(--accent-9)" }} />
                        <TextField.Root size="1" style={{ width: 60 }} value={selected.logoWidth ? String(parseInt(selected.logoWidth) || "") : ""} onChange={(e) => { const v = e.target.value; if (v === "" || /^\d+$/.test(v)) updateBlock(selected.id, { logoWidth: v ? v + "px" : "" }); }} />
                      </Flex>
                    </PropField>
                    <PropField label="Alignment">
                      <Flex gap="1">
                        {["left","center","right"].map((a) => (
                          <Button key={a} variant={(selected.align || "center") === a ? "solid" : "outline"} color="gray" size="1" style={{ flex: 1, textTransform: "capitalize" }} onClick={() => updateBlock(selected.id, { align: a })}>
                            {a}
                          </Button>
                        ))}
                      </Flex>
                    </PropField>
                  </>
                )}

                {/* COLUMNS */}
                {selected.type === "columns" && (
                  <>
                    <PropField label="Columns">
                      <Text size="2">{selected.columns.length} columns</Text>
                    </PropField>
                    <PropField label="Layout">
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                        {getColumnLayoutPresets(selected.columns.length).map((preset) => (
                          <ColumnLayoutPresetCard
                            key={`${selected.columns.length}-${preset.id}`}
                            preset={preset}
                            active={getSelectedColumnLayoutId(selected) === preset.id}
                            onSelect={() => applyColumnLayoutPreset(selected.id, preset.widths)}
                          />
                        ))}
                      </div>
                    </PropField>
                    <PropField label="Gap (px)">
                      <Flex align="center" gap="2">
                        <input type="range" min="0" max="40" step="4" value={parseInt(selected.gap) || 16} onChange={(e) => updateBlock(selected.id, { gap: e.target.value })} style={{ flex: 1, accentColor: "var(--accent-9)" }} />
                        <Text size="1" color="gray" style={{ minWidth: 30, textAlign: "right" }}>{selected.gap || "16"}px</Text>
                      </Flex>
                    </PropField>
                  </>
                )}

                {/* Delete block button */}
                <Separator size="4" />
                <Button variant="soft" color="red" size="2" onClick={() => deleteBlock(selected.id)} style={{ width: "100%" }}>
                  <TrashIcon />
                  Delete block
                </Button>
              </Flex>
            </Flex>
            </Flex>
          )}
        </Flex>

        <Dialog.Root open={sendTestOpen} onOpenChange={setSendTestOpen}>
          <Dialog.Content style={{ maxWidth: 420 }}>
            <Dialog.Title>Send test email</Dialog.Title>
            <Dialog.Description size="2" color="gray" mb="4">
              Send the saved scratch template to a test inbox.
            </Dialog.Description>
            <Flex direction="column" gap="3">
              <TextField.Root
                placeholder="name@example.com"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
              />
              <Flex direction="column" gap="1">
                <Text size="2" weight="medium">Send from</Text>
                <Select.Root value={testSenderDomainId} onValueChange={setTestSenderDomainId}>
                  <Select.Trigger style={{ width: "100%" }} />
                  <Select.Content>
                    <Select.Item value="default">Default (noreply@b-connect.co.uk)</Select.Item>
                    {(senderDomains || []).filter((d) => d.status === "verified" && d.link_status === "verified" && d.is_active).map((d) => (
                      <Select.Item key={d.sender_domain_id} value={d.sender_domain_id}>
                        {d.default_sender_name} &lt;{d.default_sender_email}&gt;
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
                <Text size="1" color="gray">Choose which sender domain to use for this test email.</Text>
              </Flex>
              {testEmailError && <Text size="1" color="red">{testEmailError}</Text>}
              <Flex justify="end" gap="2">
                <Button variant="soft" color="gray" onClick={() => setSendTestOpen(false)} disabled={isSendingTest}>Cancel</Button>
                <Button onClick={handleSendTest} loading={isSendingTest}>Send test email</Button>
              </Flex>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        <Dialog.Root open={duplicateDialogOpen} onOpenChange={(open) => { if (!open && !isDuplicating) setDuplicateDialogOpen(false); }}>
          <Dialog.Content style={{ maxWidth: 420 }}>
            <Dialog.Title>Copy email template</Dialog.Title>
            <Dialog.Description size="2" color="gray" mb="4">
              Create a duplicate of "{templateName}". The copy starts as an exact clone that you can edit independently.
            </Dialog.Description>
            <Flex direction="column" gap="3">
              <Flex direction="column" gap="1">
                <Text size="2" weight="medium">Name</Text>
                <TextField.Root
                  placeholder="Template Name"
                  value={duplicateForm.name}
                  onChange={(e) => setDuplicateForm({ ...duplicateForm, name: e.target.value })}
                />
              </Flex>
              <Flex direction="column" gap="1">
                <Text size="2" weight="medium">Subject</Text>
                <TextField.Root
                  placeholder="Email's subject line"
                  value={duplicateForm.subject}
                  onChange={(e) => setDuplicateForm({ ...duplicateForm, subject: e.target.value })}
                />
              </Flex>
              <Flex justify="end" gap="2">
                <Button variant="soft" color="gray" onClick={() => setDuplicateDialogOpen(false)} disabled={isDuplicating}>Cancel</Button>
                <Button
                  onClick={handleConfirmDuplicate}
                  disabled={!duplicateForm.name.trim() || !duplicateForm.subject.trim() || isDuplicating}
                  loading={isDuplicating}
                >
                  Create copy
                </Button>
              </Flex>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        <AlertDialog.Root open={discardAlertOpen} onOpenChange={setDiscardAlertOpen}>
          <AlertDialog.Content style={{ maxWidth: 420 }}>
            <AlertDialog.Title>Discard changes?</AlertDialog.Title>
            <AlertDialog.Description size="2">
              This will reset the scratch designer to the last saved version.
            </AlertDialog.Description>
            <Flex justify="end" gap="2" mt="4">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">Cancel</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button color="red" onClick={handleDiscardChanges}>Discard changes</Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>

        <AlertDialog.Root open={reviewLinkAlertOpen} onOpenChange={setReviewLinkAlertOpen}>
          <AlertDialog.Content style={{ maxWidth: 440 }}>
            <AlertDialog.Title>Add review destinations before saving</AlertDialog.Title>
            <AlertDialog.Description size="2">
              {invalidReviewCount > 1
                ? `${invalidReviewCount} review blocks are missing a review URL or survey template. Add both destinations before saving.`
                : "This review block is missing a review URL or survey template. Add both destinations before saving."}
            </AlertDialog.Description>
            <Flex justify="end" gap="2" mt="4">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">Close</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button onClick={handleFocusMissingReview}>Edit review block</Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      </Flex>
    <ToastAlert />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CANVAS BLOCK
   ═══════════════════════════════════════════════════════════════════ */
function CanvasBlock({
  block, idx, isSelected, selectedId, isDragging, onSelect, onSelectChild, onUpdate,
  onDelete, onDuplicate, onMoveUp, onMoveDown, onDragStart, onDragEnd,
  isFirst, isLast, isMergeTarget,
  addBlockToColumn, updateColumnBlock, deleteColumnBlock, moveColumnBlock, dropIntoColumn,
  dragType, dragNewBlockType, moveDraggedMediaBlockToColumn, onColumnMediaDragStart,
  pushHistory, allBlocks, viewMode, handleScratchFileSelection,
}) {
  const [hover, setHover] = useState(false);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const show = isSelected || hover || columnMenuOpen;
  const isMediaBlock = isImageLikeBlockType(block.type);
  const blockRef = React.useRef(null);
  const resizeState = React.useRef(null);

  const handleResizeStart = React.useCallback((e, axis) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = blockRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (pushHistory && allBlocks) pushHistory(allBlocks);
    resizeState.current = { startX: e.clientX, startY: e.clientY, startW: rect.width, startH: rect.height, axis };

    const handleResizeMove = (ev) => {
      if (!resizeState.current) return;
      const { axis: a, startX, startY, startW, startH } = resizeState.current;
      if (a === "y") {
        const delta = ev.clientY - startY;
        const newH = Math.max(0, startH + delta);
        onUpdate({ sectionHeight: String(Math.round(newH)) });
      } else if (a === "x") {
        const delta = ev.clientX - startX;
        const newW = startW + delta * 2; // *2 because block is centred
        onUpdate({ sectionMaxWidth: String(Math.round(newW)) });
      }
    };

    const handleResizeEnd = () => {
      resizeState.current = null;
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    };

    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
  }, [onUpdate, pushHistory, allBlocks]);

  const handleMediaResizeStart = React.useCallback((e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    if (pushHistory && allBlocks) pushHistory(allBlocks);

    const startWidth = block.type === "image" ? getImageSizeNumber(block) : getLogoSizeNumber(block);
    const resizeSession = { startX: e.clientX, startY: e.clientY, startWidth, corner };

    const handleResizeMove = (ev) => {
      const horizontalDirection = resizeSession.corner.includes("left") ? -1 : 1;
      const verticalDirection = resizeSession.corner.includes("top") ? -1 : 1;
      const deltaX = (ev.clientX - resizeSession.startX) * horizontalDirection;
      const deltaY = (ev.clientY - resizeSession.startY) * verticalDirection;
      const sizeDelta = Math.abs(deltaX) >= Math.abs(deltaY) ? deltaX : deltaY;
      const nextWidth = Math.max(40, Math.round(resizeSession.startWidth + sizeDelta));

      if (block.type === "image") {
        onUpdate({ imageSize: String(nextWidth), width: "", height: "" });
      } else {
        onUpdate({ logoWidth: `${nextWidth}px` });
      }
    };

    const handleResizeEnd = () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    };

    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
  }, [allBlocks, block, onUpdate, pushHistory]);

  const renderContent = () => {
    switch (block.type) {
      case "columns":
        return (
          <ColumnsRenderer
            block={block}
            selectedId={selectedId}
            onSelectChild={onSelectChild}
            addBlockToColumn={addBlockToColumn}
            updateColumnBlock={updateColumnBlock}
            deleteColumnBlock={deleteColumnBlock}
            moveColumnBlock={moveColumnBlock}
            dropIntoColumn={dropIntoColumn}
            dragType={dragType}
            dragNewBlockType={dragNewBlockType}
            moveDraggedMediaBlockToColumn={moveDraggedMediaBlockToColumn}
            onMenuOpenChange={setColumnMenuOpen}
            onColumnMediaDragStart={onColumnMediaDragStart}
            onDragEnd={onDragEnd}
            viewMode={viewMode}
            handleScratchFileSelection={handleScratchFileSelection}
          />
        );
      case "heading":
        return (
          <div
            contentEditable suppressContentEditableWarning
            onBlur={(e) => onUpdate({ text: e.currentTarget.innerText })}
            style={{ fontSize: block.fontSize || "28px", fontWeight: block.fontWeight || "700", fontFamily: block.fontFamily || "inherit", color: block.color || "#111827", textAlign: block.textAlign || "center", lineHeight: block.lineHeight || "1.3", padding: "20px 30px", outline: "none", cursor: "text", whiteSpace: "pre-wrap" }}
          >
            {block.text}
          </div>
        );
      case "paragraph":
        return (
          <div
            contentEditable suppressContentEditableWarning
            onBlur={(e) => onUpdate({ text: e.currentTarget.innerText })}
            style={{ fontSize: block.fontSize || "16px", fontWeight: block.fontWeight || "400", fontFamily: block.fontFamily || "inherit", color: block.color || "#111827", textAlign: block.textAlign || "left", lineHeight: block.lineHeight || "1.6", padding: "10px 30px", outline: "none", cursor: "text", whiteSpace: "pre-wrap" }}
          >
            {block.text}
          </div>
        );
      case "image": {
        const imageLinkUrl = getMediaLinkUrl(block);
        return (
          <div style={{ padding: block.fullBleed ? "0" : "10px 30px", textAlign: block.align || "center" }}>
            {block.src ? (
              <div draggable onDragStart={onDragStart} onDragEnd={onDragEnd} style={{ display: "block", position: "relative", width: getImageWidthValue(block), maxWidth: "100%", cursor: "grab", ...(block.align === "center" ? { marginLeft: "auto", marginRight: "auto" } : block.align === "right" ? { marginLeft: "auto" } : {}) }}>
                {imageLinkUrl ? (
                  <a href={imageLinkUrl} onClick={(e) => e.preventDefault()} style={{ display: "block", textDecoration: "none" }}>
                    <img src={block.src} alt={block.alt || ""} style={{ width: "100%", maxWidth: "100%", borderRadius: block.borderRadius || "0", display: "block", objectFit: "contain" }} />
                  </a>
                ) : (
                  <img src={block.src} alt={block.alt || ""} style={{ width: "100%", maxWidth: "100%", borderRadius: block.borderRadius || "0", display: "block", objectFit: "contain" }} />
                )}
                {show && <MediaCornerHandles onResizeStart={handleMediaResizeStart} />}
                {show && (
                  <label style={{ position: "absolute", bottom: 8, right: 8, padding: "5px 10px", background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: "pointer", backdropFilter: "blur(4px)" }}>
                    Replace
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                      const f = e.target.files?.[0];
                      handleScratchFileSelection(f, (previewSrc, assetKey) => onUpdate({ src: previewSrc, srcAssetKey: assetKey }), block.srcAssetKey);
                      e.target.value = "";
                    }} />
                  </label>
                )}
              </div>
            ) : (
              <label style={{ display: "block", background: "var(--gray-a3)", borderRadius: "var(--radius-2)", padding: "32px 20px", border: "2px dashed var(--gray-a6)", color: "var(--gray-9)", fontSize: 13, cursor: "pointer", textAlign: "center" }}>
                <div style={{ marginBottom: 6 }}>
                  <ImageIcon width={32} height={32} style={{ opacity: 0.5 }} />
                </div>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Click to upload image</div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>or paste URL in properties</div>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                  const f = e.target.files?.[0];
                  handleScratchFileSelection(f, (previewSrc, assetKey) => onUpdate({ src: previewSrc, srcAssetKey: assetKey }), block.srcAssetKey);
                  e.target.value = "";
                }} />
              </label>
            )}
          </div>
        );
      }
      case "button":
        return (
          <div style={{ padding: "16px 30px", textAlign: block.align || "center" }}>
            <a
              href={ensureAbsoluteUrl(block.url) || "https://example.com"} onClick={(e) => e.preventDefault()}
              style={{
                display: block.fullWidth ? "block" : "inline-block",
                padding: block.padding || "14px 32px",
                background: block.bgColor || "#4F46E5",
                color: block.textColor || "#ffffff",
                fontSize: block.fontSize || "16px",
                fontWeight: block.fontWeight || "600",
                fontFamily: block.fontFamily || "inherit",
                borderRadius: block.borderRadius || "6px",
                textDecoration: "none",
                textAlign: "center",
                border: parseInt(block.borderWidth) > 0 ? `${block.borderWidth}px solid ${block.borderColor || "#000"}` : "none",
              }}
            >
              {block.text || "Learn more"}
            </a>
          </div>
        );
      case "review":
        return <ReviewBlockCard block={block} />;
      case "divider":
        return (
          <div style={{ padding: "8px 30px" }}>
            <hr style={{ border: "none", borderTop: `${block.thickness || "1px"} ${block.style || "solid"} ${block.color || "#E5E7EB"}`, width: block.dividerWidth || "100%", margin: "0 auto" }} />
          </div>
        );
      case "spacer":
        return (
          <div style={{ height: block.spacerHeight || "40px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {show && <Text size="1" color="gray" style={{ background: "var(--gray-a3)", padding: "2px 8px", borderRadius: 4 }}>Spacer ({block.spacerHeight || "40px"})</Text>}
          </div>
        );
      case "social":
        return (
          <SocialItemsRenderer block={block} editing={show} />
        );
      case "logo": {
        const logoLinkUrl = getMediaLinkUrl(block);
        return (
          <div style={{ padding: "16px 30px", textAlign: block.align || "center" }}>
            {block.src ? (
              <div draggable onDragStart={onDragStart} onDragEnd={onDragEnd} style={{ display: "inline-block", position: "relative", cursor: "grab" }}>
                {logoLinkUrl ? (
                  <a href={logoLinkUrl} onClick={(e) => e.preventDefault()} style={{ display: "inline-block", textDecoration: "none" }}>
                    <img src={block.src} alt="Logo" style={{ width: block.logoWidth || "150px", display: "inline-block" }} />
                  </a>
                ) : (
                  <img src={block.src} alt="Logo" style={{ width: block.logoWidth || "150px", display: "inline-block" }} />
                )}
                {show && <MediaCornerHandles onResizeStart={handleMediaResizeStart} />}
              </div>
            ) : (
              <label style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "14px 24px", border: "2px dashed var(--gray-a6)", borderRadius: "var(--radius-2)", color: "var(--gray-9)", fontSize: 12, cursor: "pointer" }}>
                Click to upload logo
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                  const f = e.target.files?.[0];
                  handleScratchFileSelection(f, (previewSrc, assetKey) => onUpdate({ src: previewSrc, srcAssetKey: assetKey }), block.srcAssetKey);
                  e.target.value = "";
                }} />
              </label>
            )}
          </div>
        );
      }
      default:
        return <div style={{ padding: 20 }}><Text color="gray">Unknown block</Text></div>;
    }
  };

  return (
    <div
      ref={blockRef}
      data-block-idx={idx}
      data-top-level-block="true"
      data-block-id={block.id}
      data-block-type={block.type}
      className={`scratch-canvas-block${isSelected ? " selected" : ""}${isDragging ? " dragging" : ""}${isMergeTarget ? " merge-target" : ""}`}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        cursor: "pointer",
        background: block.sectionBg && block.sectionBg !== "transparent" ? block.sectionBg : "transparent",
        padding: block.sectionPadding && block.sectionPadding !== "0" ? `${block.sectionPadding}px` : "0",
        maxWidth: block.sectionMaxWidth ? `${block.sectionMaxWidth}px` : undefined,
        margin: block.sectionMaxWidth ? "0 auto" : undefined,
        overflow: "visible",
        zIndex: columnMenuOpen ? 60 : show ? 20 : 1,
        isolation: "isolate",
      }}
    >
      {/* Grip handle */}
      {show && (
        <div
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          className="scratch-grip"
          style={{
            position: "absolute", left: -2, top: "50%", transform: "translateY(-50%)",
            width: 20, height: 32,
            background: "var(--color-background)", border: "1px solid var(--gray-a5)", borderRadius: "4px 0 0 4px",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--gray-9)", zIndex: 40, boxShadow: "-2px 0 4px rgba(0,0,0,0.06)",
            opacity: 1,
          }}
          title={isMediaBlock ? "Drag to move" : "Drag to reorder"}
        >
          <DragHandleDots2Icon width={12} height={12} />
        </div>
      )}

      <SectionContentScaler block={block}>
        {renderContent()}
      </SectionContentScaler>

      {/* Toolbar */}
      {show && (
        <Flex
          gap="1"
          style={{
            position: "absolute", top: -1, right: -1,
            background: "var(--color-background)", border: "1px solid var(--gray-a5)", borderRadius: "var(--radius-2)",
            padding: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", zIndex: 70,
          }}
        >
          {!isFirst && (
            <Tooltip content="Move up">
              <IconButton variant="ghost" color="gray" size="1" onClick={(e) => { e.stopPropagation(); onMoveUp(); }}>
                <ChevronUpIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isLast && (
            <Tooltip content="Move down">
              <IconButton variant="ghost" color="gray" size="1" onClick={(e) => { e.stopPropagation(); onMoveDown(); }}>
                <ChevronDownIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip content="Duplicate">
            <IconButton variant="ghost" color="gray" size="1" onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
              <CopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip content="Delete">
            <IconButton variant="ghost" color="red" size="1" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
              <TrashIcon />
            </IconButton>
          </Tooltip>
        </Flex>
      )}

      {/* Resize handle at bottom (height) */}
      {show && !isMediaBlock && (
        <div
          onMouseDown={(e) => handleResizeStart(e, "y")}
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: -4,
            width: 32,
            height: 8,
            cursor: "ns-resize",
            background: "var(--accent-9)",
            opacity: isSelected ? 0.6 : 0,
            transition: "opacity 0.15s",
            zIndex: 40,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = isSelected ? "0.6" : "0"; }}
          title="Drag to resize height"
        >
          <svg width="14" height="4" viewBox="0 0 14 4" fill="white"><circle cx="3" cy="2" r="1"/><circle cx="7" cy="2" r="1"/><circle cx="11" cy="2" r="1"/></svg>
        </div>
      )}

      {/* Resize handle at right (width) */}
      {show && !isMediaBlock && (
        <div
          onMouseDown={(e) => handleResizeStart(e, "x")}
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            right: -4,
            width: 8,
            height: 32,
            cursor: "ew-resize",
            background: "var(--accent-9)",
            opacity: isSelected ? 0.6 : 0,
            transition: "opacity 0.15s",
            zIndex: 40,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = isSelected ? "0.6" : "0"; }}
          title="Drag to resize width"
        >
          <svg width="4" height="14" viewBox="0 0 4 14" fill="white"><circle cx="2" cy="3" r="1"/><circle cx="2" cy="7" r="1"/><circle cx="2" cy="11" r="1"/></svg>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   COLUMNS RENDERER
   ═══════════════════════════════════════════════════════════════════ */
function ColumnsRenderer({ block, selectedId, onSelectChild, addBlockToColumn, updateColumnBlock, deleteColumnBlock, moveColumnBlock, dropIntoColumn, dragType, dragNewBlockType, moveDraggedMediaBlockToColumn, onMenuOpenChange, onColumnMediaDragStart, onDragEnd, viewMode, handleScratchFileSelection }) {
  const isMobile = viewMode === "mobile";
  const columnWidths = getNormalizedColumnWidths(block);
  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: parseInt(block.gap) || 16, padding: "8px 12px", minHeight: 80, alignItems: isMobile ? "stretch" : "center", width: "100%", minWidth: 0, boxSizing: "border-box" }}>
      {block.columns.map((col, ci) => (
        <ColumnCell
          key={col.id}
          col={col}
          colIndex={ci}
          widthPercent={columnWidths[ci] || (100 / Math.max(block.columns.length || 1, 1))}
          isMobile={isMobile}
          parentId={block.id}
          selectedId={selectedId}
          onSelectChild={onSelectChild}
          addBlockToColumn={addBlockToColumn}
          updateColumnBlock={updateColumnBlock}
          deleteColumnBlock={deleteColumnBlock}
          moveColumnBlock={moveColumnBlock}
          dropIntoColumn={dropIntoColumn}
          dragType={dragType}
          dragNewBlockType={dragNewBlockType}
          moveDraggedMediaBlockToColumn={moveDraggedMediaBlockToColumn}
          onMenuOpenChange={onMenuOpenChange}
          onColumnMediaDragStart={onColumnMediaDragStart}
          onDragEnd={onDragEnd}
          handleScratchFileSelection={handleScratchFileSelection}
        />
      ))}
    </div>
  );
}

function ColumnCell({ col, colIndex, widthPercent, isMobile, parentId, selectedId, onSelectChild, addBlockToColumn, updateColumnBlock, deleteColumnBlock, moveColumnBlock, dropIntoColumn, dragType, dragNewBlockType, moveDraggedMediaBlockToColumn, onMenuOpenChange, onColumnMediaDragStart, onDragEnd, handleScratchFileSelection }) {
  const [dragOver, setDragOver] = useState(false);
  const [dropIndex, setDropIndex] = useState(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const cellRef = useRef(null);

  useEffect(() => {
    onMenuOpenChange?.(addMenuOpen);
    return () => onMenuOpenChange?.(false);
  }, [addMenuOpen, onMenuOpenChange]);

  const canAcceptDrop = (dragType === "media-move") || (dragType === "new" && dragNewBlockType && !dragNewBlockType.startsWith("columns"));
  const computeColumnDropIndex = (e) => {
    const childBlocks = cellRef.current?.querySelectorAll("[data-column-block-idx]");
    if (!childBlocks?.length) return col.blocks.length;

    for (let i = 0; i < childBlocks.length; i += 1) {
      const rect = childBlocks[i].getBoundingClientRect();
      if (e.clientY < rect.top + rect.height / 2) return i;
    }

    return col.blocks.length;
  };
  const handleDragOver = (e) => {
    if (!canAcceptDrop) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
    setDropIndex(computeColumnDropIndex(e));
  };
  const handleDragLeave = (e) => {
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOver(false);
      setDropIndex(null);
    }
  };
  const handleDrop = (e) => {
    if (!canAcceptDrop) return;
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const data = e.dataTransfer.getData("text/plain");
    const nextDropIndex = dropIndex ?? col.blocks.length;
    setDropIndex(null);
    if (data.startsWith("new:")) {
      const type = data.replace("new:", "");
      if (!type.startsWith("columns")) {
        dropIntoColumn(parentId, col.id, type, nextDropIndex);
      }
    } else {
      moveDraggedMediaBlockToColumn?.(parentId, col.id, nextDropIndex);
    }
    onDragEnd?.();
  };

  return (
    <div
      ref={cellRef}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`scratch-col-cell${dragOver ? " drag-over" : ""}${hover ? "" : " idle"}`}
      style={{ flex: isMobile ? "1 1 auto" : `${Math.max(widthPercent, 1)} 1 0`, minWidth: 0, width: isMobile ? "100%" : undefined, display: "flex", flexDirection: "column", justifyContent: "center", position: "relative", borderColor: hover || dragOver ? undefined : "transparent", zIndex: addMenuOpen ? 50 : undefined }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={(e) => {
        // Don't close if the add menu is open (dropdown may overflow the column cell)
        if (addMenuOpen) return;
        setHover(false);
      }}
    >
      {col.blocks.length === 0 ? (
        <Flex direction="column" align="center" justify="center" style={{ flex: 1, padding: "16px 8px", gap: 8 }}>
          {(hover || dragOver) ? (
            <>
              <Text size="1" color="gray">{dragOver ? "Drop here" : `Column ${colIndex + 1}`}</Text>
              <div style={{ position: "relative" }}>
                <IconButton
                  variant="ghost" color="gray" size="1" radius="full"
                  onClick={(e) => { e.stopPropagation(); setAddMenuOpen(!addMenuOpen); }}
                  style={{ border: "1.5px dashed #9ca3af", color: "#6b7280" }}
                >
                  <PlusIcon />
                </IconButton>
                {addMenuOpen && <ColumnAddMenu items={CONTENT_BLOCKS} onAdd={(type) => { addBlockToColumn(parentId, col.id, type); setAddMenuOpen(false); }} onClose={() => setAddMenuOpen(false)} />}
              </div>
            </>
          ) : (
            <Text size="1" color="gray" style={{ opacity: 0.4 }}>{`Col ${colIndex + 1}`}</Text>
          )}
        </Flex>
      ) : (
        <Flex direction="column" gap="0">
          {col.blocks.map((cb, cbi) => (
            <React.Fragment key={cb.id}>
              {dragOver && dropIndex === cbi && <div className="scratch-drop-indicator" />}
              <ColumnChildBlock
                block={cb}
                blockIndex={cbi}
                isSelected={selectedId === cb.id}
                onSelect={() => onSelectChild(cb.id)}
                onUpdate={(u) => updateColumnBlock(parentId, col.id, cb.id, u)}
                onDelete={() => deleteColumnBlock(parentId, col.id, cb.id)}
                onMoveUp={() => moveColumnBlock(parentId, col.id, cb.id, -1)}
                onMoveDown={() => moveColumnBlock(parentId, col.id, cb.id, 1)}
                onDragStart={isImageLikeBlockType(cb.type) ? (e) => onColumnMediaDragStart?.(e, parentId, col.id, cb.id) : undefined}
                onDragEnd={onDragEnd}
                isFirst={cbi === 0}
                isLast={cbi === col.blocks.length - 1}
                handleScratchFileSelection={handleScratchFileSelection}
              />
            </React.Fragment>
          ))}
          {dragOver && dropIndex === col.blocks.length && <div className="scratch-drop-indicator" />}
          {hover && (
            <Flex justify="center" py="1">
              <div style={{ position: "relative" }}>
                <IconButton
                  variant="ghost" color="gray" size="1" radius="full"
                  onClick={(e) => { e.stopPropagation(); setAddMenuOpen(!addMenuOpen); }}
                  style={{ border: "1.5px dashed #9ca3af", color: "#6b7280" }}
                >
                  <PlusIcon />
                </IconButton>
                {addMenuOpen && <ColumnAddMenu items={CONTENT_BLOCKS} onAdd={(type) => { addBlockToColumn(parentId, col.id, type); setAddMenuOpen(false); }} onClose={() => setAddMenuOpen(false)} />}
              </div>
            </Flex>
          )}
        </Flex>
      )}
    </div>
  );
}

function ColumnAddMenu({ items, onAdd, onClose }) {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ top: 32, left: "50%" });

  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportH = window.innerHeight;
      if (rect.bottom > viewportH - 10) {
        setPosition({ bottom: 32, left: "50%" });
      }
    }
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <Box
      ref={menuRef}
      style={{
        position: "absolute",
        ...(position.bottom ? { bottom: position.bottom } : { top: position.top }),
        left: position.left,
        transform: "translateX(-50%)",
        background: "var(--color-background)", border: "1px solid var(--gray-a5)", borderRadius: "var(--radius-3)",
        padding: 6, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 50,
        display: "flex", flexDirection: "column", gap: 2, minWidth: 130,
        maxHeight: 220, overflowY: "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((b) => (
        <button
          key={b.type}
          onClick={(e) => { e.stopPropagation(); onAdd(b.type); }}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "6px 10px", borderRadius: "var(--radius-2)", border: "none",
            background: "transparent", cursor: "pointer", fontSize: 12,
            color: "var(--gray-12)", fontFamily: "inherit", textAlign: "left", width: "100%",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--gray-a3)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          {b.label}
        </button>
      ))}
    </Box>
  );
}

function ColumnLayoutPresetCard({ preset, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        width: "100%",
        padding: "10px",
        borderRadius: "var(--radius-3)",
        border: `1px solid ${active ? "var(--accent-a7)" : "var(--gray-a5)"}`,
        background: active ? "var(--accent-a3)" : "var(--gray-a2)",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ display: "flex", gap: 4, alignItems: "stretch", flex: 1, minWidth: 0, height: 24 }}>
          {preset.widths.map((width, index) => (
            <div
              key={`${preset.id}-${index}`}
              style={{
                flex: Math.max(width, 1),
                minWidth: 0,
                borderRadius: 4,
                border: `1px solid ${active ? "var(--accent-a7)" : "var(--gray-a6)"}`,
                background: active ? "var(--accent-a5)" : "var(--gray-a4)",
              }}
            />
          ))}
        </div>
        <div
          style={{
            width: 11,
            height: 11,
            flexShrink: 0,
            borderRadius: 999,
            border: `1.5px solid ${active ? "var(--accent-9)" : "var(--gray-a6)"}`,
            background: active ? "var(--accent-9)" : "transparent",
          }}
        />
      </div>
      <Text size="1" style={{ color: active ? "var(--accent-11)" : "var(--gray-11)", fontWeight: 600 }}>
        {preset.label}
      </Text>
    </button>
  );
}

function ColumnChildBlock({ block, blockIndex, isSelected, onSelect, onUpdate, onDelete, onMoveUp, onMoveDown, onDragStart, onDragEnd, isFirst, isLast, handleScratchFileSelection }) {
  const [hover, setHover] = useState(false);
  const show = isSelected || hover;
  const isMediaBlock = isImageLikeBlockType(block.type);
  const blockRef = useRef(null);
  const resizeState = useRef(null);

  const handleResizeStart = useCallback((e, axis) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = blockRef.current?.getBoundingClientRect();
    if (!rect) return;
    resizeState.current = { startX: e.clientX, startY: e.clientY, startW: rect.width, startH: rect.height, axis };

    const handleResizeMove = (ev) => {
      if (!resizeState.current) return;
      const { axis: a, startX, startY, startW, startH } = resizeState.current;
      if (a === "y") {
        const delta = ev.clientY - startY;
        onUpdate({ sectionHeight: String(Math.max(0, Math.round(startH + delta))) });
      } else {
        const delta = ev.clientX - startX;
        onUpdate({ sectionMaxWidth: String(Math.max(0, Math.round(startW + delta * 2))) });
      }
    };

    const handleResizeEnd = () => {
      resizeState.current = null;
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    };

    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
  }, [onUpdate]);

  const handleMediaResizeStart = useCallback((e, corner) => {
    e.preventDefault();
    e.stopPropagation();

    const startWidth = block.type === "image" ? getImageSizeNumber(block) : getLogoSizeNumber(block);
    const resizeSession = { startX: e.clientX, startY: e.clientY, startWidth, corner };

    const handleResizeMove = (ev) => {
      const horizontalDirection = resizeSession.corner.includes("left") ? -1 : 1;
      const verticalDirection = resizeSession.corner.includes("top") ? -1 : 1;
      const deltaX = (ev.clientX - resizeSession.startX) * horizontalDirection;
      const deltaY = (ev.clientY - resizeSession.startY) * verticalDirection;
      const sizeDelta = Math.abs(deltaX) >= Math.abs(deltaY) ? deltaX : deltaY;
      const nextWidth = Math.max(32, Math.round(resizeSession.startWidth + sizeDelta));

      if (block.type === "image") {
        onUpdate({ imageSize: String(nextWidth), width: "", height: "" });
      } else {
        onUpdate({ logoWidth: `${nextWidth}px` });
      }
    };

    const handleResizeEnd = () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    };

    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
  }, [block, onUpdate]);

  const renderContent = () => {
    switch (block.type) {
      case "heading":
        return <div contentEditable suppressContentEditableWarning onBlur={(e) => onUpdate({ text: e.currentTarget.innerText })} style={{ fontSize: block.fontSize || "22px", fontWeight: block.fontWeight || "700", fontFamily: block.fontFamily || "inherit", color: block.color || "#111827", textAlign: block.textAlign || "center", lineHeight: block.lineHeight || "1.3", padding: "8px 12px", outline: "none", cursor: "text", whiteSpace: "pre-wrap" }}>{block.text}</div>;
      case "paragraph":
        return <div contentEditable suppressContentEditableWarning onBlur={(e) => onUpdate({ text: e.currentTarget.innerText })} style={{ fontSize: block.fontSize || "14px", fontWeight: block.fontWeight || "400", fontFamily: block.fontFamily || "inherit", color: block.color || "#111827", textAlign: block.textAlign || "center", lineHeight: block.lineHeight || "1.6", padding: "6px 12px", outline: "none", cursor: "text", whiteSpace: "pre-wrap" }}>{block.text}</div>;
      case "image": {
        const imageLinkUrl = getMediaLinkUrl(block);
        return (
          <div style={{ padding: "4px 8px" }}>
            {block.src ? (
              <div draggable={Boolean(onDragStart)} onDragStart={onDragStart} onDragEnd={onDragEnd} style={{ display: "block", position: "relative", width: getImageWidthValue(block), maxWidth: "100%", cursor: onDragStart ? "grab" : "default", ...(block.align === "center" ? { marginLeft: "auto", marginRight: "auto" } : block.align === "right" ? { marginLeft: "auto" } : {}) }}>
                {imageLinkUrl ? (
                  <a href={imageLinkUrl} onClick={(e) => e.preventDefault()} style={{ display: "block", textDecoration: "none" }}>
                    <img src={block.src} alt={block.alt || ""} style={{ width: "100%", maxWidth: "100%", borderRadius: block.borderRadius || "0", display: "block", objectFit: "contain" }} />
                  </a>
                ) : (
                  <img src={block.src} alt={block.alt || ""} style={{ width: "100%", maxWidth: "100%", borderRadius: block.borderRadius || "0", display: "block", objectFit: "contain" }} />
                )}
                {show && <MediaCornerHandles onResizeStart={handleMediaResizeStart} compact />}
              </div>
            ) : (
              <label style={{ display: "block", background: "var(--gray-a3)", borderRadius: "var(--radius-2)", padding: "16px 8px", border: "2px dashed var(--gray-a6)", color: "var(--gray-9)", fontSize: 11, cursor: "pointer", textAlign: "center" }}>
                + Image
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                  const f = e.target.files?.[0];
                  handleScratchFileSelection(f, (previewSrc, assetKey) => onUpdate({ src: previewSrc, srcAssetKey: assetKey }), block.srcAssetKey);
                  e.target.value = "";
                }} />
              </label>
            )}
          </div>
        );
      }
      case "button":
        return (
          <div style={{ padding: "8px 12px", textAlign: block.align || "center" }}>
            <a href={ensureAbsoluteUrl(block.url) || "https://example.com"} onClick={(e) => e.preventDefault()} style={{ display: block.fullWidth ? "block" : "inline-block", padding: block.padding || "10px 20px", background: block.bgColor || "#4F46E5", color: block.textColor || "#ffffff", fontSize: block.fontSize || "14px", fontWeight: block.fontWeight || "600", borderRadius: block.borderRadius || "6px", textDecoration: "none", textAlign: "center" }}>{block.text || "Button"}</a>
          </div>
        );
      case "review":
        return <ReviewBlockCard block={block} compact />;
      case "divider":
        return <div style={{ padding: "4px 8px" }}><hr style={{ border: "none", borderTop: `${block.thickness || "1px"} ${block.style || "solid"} ${block.color || "#E5E7EB"}` }} /></div>;
      case "spacer":
        return <div style={{ height: block.spacerHeight || "20px" }} />;
      case "social":
        return <SocialItemsRenderer block={block} compact />;
      case "logo": {
        const logoLinkUrl = getMediaLinkUrl(block);
        return (
          <div style={{ padding: "4px 8px" }}>
            {block.src ? (
              <div draggable={Boolean(onDragStart)} onDragStart={onDragStart} onDragEnd={onDragEnd} style={{ display: "block", position: "relative", width: block.logoWidth || "150px", maxWidth: "100%", cursor: onDragStart ? "grab" : "default", ...(block.align === "center" ? { marginLeft: "auto", marginRight: "auto" } : block.align === "right" ? { marginLeft: "auto" } : {}) }}>
                {logoLinkUrl ? (
                  <a href={logoLinkUrl} onClick={(e) => e.preventDefault()} style={{ display: "block", textDecoration: "none" }}>
                    <img src={block.src} alt="Logo" style={{ width: "100%", maxWidth: "100%", display: "block", objectFit: "contain" }} />
                  </a>
                ) : (
                  <img src={block.src} alt="Logo" style={{ width: "100%", maxWidth: "100%", display: "block", objectFit: "contain" }} />
                )}
                {show && <MediaCornerHandles onResizeStart={handleMediaResizeStart} compact />}
              </div>
            ) : (
              <label style={{ display: "block", background: "var(--gray-a3)", borderRadius: "var(--radius-2)", padding: "16px 8px", border: "2px dashed var(--gray-a6)", color: "var(--gray-9)", fontSize: 11, cursor: "pointer", textAlign: "center" }}>
                + Logo
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                  const f = e.target.files?.[0];
                  handleScratchFileSelection(f, (previewSrc, assetKey) => onUpdate({ src: previewSrc, srcAssetKey: assetKey }), block.srcAssetKey);
                  e.target.value = "";
                }} />
              </label>
            )}
          </div>
        );
      }
      default:
        return <div style={{ padding: 8, fontSize: 12 }}>Block</div>;
    }
  };

  return (
    <div
      ref={blockRef}
      data-column-block-idx={blockIndex}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        border: isSelected ? "2px solid var(--accent-9)" : hover ? "1px solid var(--gray-a5)" : "1px solid transparent",
        borderRadius: "var(--radius-1)",
        transition: "border-color 0.1s",
        cursor: "pointer",
        margin: 2,
        background: block.sectionBg && block.sectionBg !== "transparent" ? block.sectionBg : "transparent",
        padding: block.sectionPadding && block.sectionPadding !== "0" ? `${block.sectionPadding}px` : "0",
        overflow: "visible",
        zIndex: show ? 20 : 1,
        isolation: "isolate",
      }}
    >
      {show && isMediaBlock && onDragStart && (
        <div
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          style={{
            position: "absolute",
            left: -2,
            top: "50%",
            transform: "translateY(-50%)",
            width: 18,
            height: 28,
            background: "var(--color-background)",
            border: "1px solid var(--gray-a5)",
            borderRadius: "4px 0 0 4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--gray-9)",
            zIndex: 40,
            boxShadow: "-2px 0 4px rgba(0,0,0,0.06)",
          }}
          title="Drag to move"
        >
          <DragHandleDots2Icon width={10} height={10} />
        </div>
      )}
      <SectionContentScaler block={block} compact>
        {renderContent()}
      </SectionContentScaler>
      {show && (
        <Flex
          gap="1"
          style={{
            position: "absolute", top: -1, right: -1,
            background: "var(--color-background)", border: "1px solid var(--gray-a5)", borderRadius: "var(--radius-1)",
            padding: 1, boxShadow: "0 1px 4px rgba(0,0,0,0.1)", zIndex: 70,
          }}
        >
          {!isFirst && <IconButton variant="ghost" color="gray" size="1" onClick={(e) => { e.stopPropagation(); onMoveUp(); }}><ChevronUpIcon /></IconButton>}
          {!isLast && <IconButton variant="ghost" color="gray" size="1" onClick={(e) => { e.stopPropagation(); onMoveDown(); }}><ChevronDownIcon /></IconButton>}
          <IconButton variant="ghost" color="red" size="1" onClick={(e) => { e.stopPropagation(); onDelete(); }}><TrashIcon /></IconButton>
        </Flex>
      )}
      {show && !isMediaBlock && (
        <div
          onMouseDown={(e) => handleResizeStart(e, "y")}
          style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", bottom: -4, width: 28, height: 8, cursor: "ns-resize", background: "var(--accent-9)", opacity: isSelected ? 0.65 : 0, transition: "opacity 0.15s", zIndex: 40, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = isSelected ? "0.65" : "0"; }}
          title="Drag to resize height"
        >
          <svg width="14" height="4" viewBox="0 0 14 4" fill="white"><circle cx="3" cy="2" r="1"/><circle cx="7" cy="2" r="1"/><circle cx="11" cy="2" r="1"/></svg>
        </div>
      )}
      {show && !isMediaBlock && (
        <div
          onMouseDown={(e) => handleResizeStart(e, "x")}
          style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", right: -4, width: 8, height: 28, cursor: "ew-resize", background: "var(--accent-9)", opacity: isSelected ? 0.65 : 0, transition: "opacity 0.15s", zIndex: 40, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = isSelected ? "0.65" : "0"; }}
          title="Drag to resize width"
        >
          <svg width="4" height="14" viewBox="0 0 4 14" fill="white"><circle cx="2" cy="3" r="1"/><circle cx="2" cy="7" r="1"/><circle cx="2" cy="11" r="1"/></svg>
        </div>
      )}
    </div>
  );
}

function MediaCornerHandles({ onResizeStart, compact }) {
  const handleSize = compact ? 8 : 10;
  const offset = compact ? -4 : -5;
  const handles = [
    { key: "top-left", cursor: "nwse-resize", style: { top: offset, left: offset } },
    { key: "top-right", cursor: "nesw-resize", style: { top: offset, right: offset } },
    { key: "bottom-left", cursor: "nesw-resize", style: { bottom: offset, left: offset } },
    { key: "bottom-right", cursor: "nwse-resize", style: { bottom: offset, right: offset } },
  ];

  return handles.map((handle) => (
    <div
      key={handle.key}
      onMouseDown={(e) => onResizeStart(e, handle.key)}
      style={{
        position: "absolute",
        width: handleSize,
        height: handleSize,
        borderRadius: 2,
        background: "#ffffff",
        border: "2px solid var(--accent-9)",
        boxShadow: "0 1px 4px rgba(0,0,0,0.16)",
        zIndex: 35,
        cursor: handle.cursor,
        ...handle.style,
      }}
    />
  ));
}

/* ═══════════════════════════════════════════════════════════════════
   SECTION CONTENT SCALER
   ═══════════════════════════════════════════════════════════════════ */
function SectionContentScaler({ block, children, compact }) {
  const contentRef = useRef(null);
  const [naturalHeight, setNaturalHeight] = useState(0);
  const targetHeight = parseInt(block.sectionHeight) || 0;

  useEffect(() => {
    if (!contentRef.current) return undefined;
    const element = contentRef.current;
    const measure = () => setNaturalHeight(element.scrollHeight || element.getBoundingClientRect().height || 0);
    measure();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => measure());
    observer.observe(element);
    return () => observer.disconnect();
  }, [children, block.sectionHeight, block.sectionPadding, block.sectionMaxWidth, compact]);

  const scale = targetHeight && naturalHeight ? Math.min(1, targetHeight / naturalHeight) : 1;
  const outerStyle = targetHeight ? { height: `${targetHeight}px`, overflow: "hidden", position: "relative" } : undefined;
  const innerStyle = scale < 1 ? { transform: `scale(${scale})`, transformOrigin: "top left", width: `${100 / scale}%` } : undefined;

  return (
    <div style={outerStyle}>
      <div ref={contentRef} style={innerStyle}>
        {children}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   SOCIAL ITEMS RENDERER
   ═══════════════════════════════════════════════════════════════════ */
const SOCIAL_ICONS = Object.fromEntries(
  Object.entries(SOCIAL_ICON_DEFINITIONS).map(([platform, definition]) => [
    platform,
    (size, color) => (
      <svg width={size} height={size} viewBox={definition.viewBox} fill={color} style={{ display: "block" }}>
        {definition.paths.map((path) => <path key={path} d={path} />)}
      </svg>
    ),
  ]),
);

function SocialItemsRenderer({ block, compact, editing }) {
  const items = block.items || [];
  const sz = compact ? Math.round((parseInt(block.iconSize) || 32) * 0.75) : parseInt(block.iconSize) || 32;
  const gap = compact ? 8 : parseInt(block.iconSpacing) || 12;
  const color = block.iconColor || "#111827";
  const textColor = block.textColor || color;
  const textFontSize = block.textFontSize || (compact ? "11px" : "13px");
  const textFontWeight = block.textFontWeight || "500";
  const textFontFamily = block.textFontFamily || "inherit";

  if (!items.length) {
    return <div style={{ padding: compact ? "8px" : "16px 30px", textAlign: "center", color: "var(--gray-8)", fontSize: 12 }}>No social items — select block to add</div>;
  }

  return (
    <div style={{ padding: compact ? "8px" : "16px 30px", display: "flex", justifyContent: block.align || "center", alignItems: "center", gap, flexWrap: "wrap" }}>
      {items.map((item) => {
        if (item.type === "spacer") {
          if (!editing && !compact) return <div key={item.id} style={{ width: parseInt(item.width) || 16, minWidth: parseInt(item.width) || 16 }} />;
          return <div key={item.id} style={{ width: parseInt(item.width) || 16, minWidth: parseInt(item.width) || 16, height: compact ? 18 : 24, border: "1px dashed var(--gray-a6)", borderRadius: 4, background: "repeating-linear-gradient(90deg, transparent 0 6px, var(--gray-a3) 6px 8px)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--gray-8)", fontSize: compact ? 8 : 9 }} title={`Spacer ${item.width || "16"}px`}>{!compact ? "Spacer" : ""}</div>;
        }
        if (item.type === "text") {
          return (
            <a key={item.id} href={item.url || "#"} onClick={(e) => e.preventDefault()} style={{ fontSize: textFontSize, color: textColor, textDecoration: "none", fontWeight: textFontWeight, fontFamily: textFontFamily }}>
              {item.label || "Link"}
            </a>
          );
        }
        const customIcon = item.iconMode === "upload" && item.customIconSrc;
        const iconFn = SOCIAL_ICONS[item.platform];
        return (
          <a key={item.id} href={item.url || "#"} onClick={(e) => e.preventDefault()} style={{ display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 0 }}>
            {customIcon ? (
              <img src={item.customIconSrc} alt={item.label || item.platform || "icon"} style={{ width: sz, height: sz, objectFit: "contain", display: "block" }} />
            ) : iconFn ? iconFn(sz, color) : <div style={{ width: sz, height: sz, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: sz * 0.45, fontWeight: 700 }}>{(item.platform || "?")[0].toUpperCase()}</div>}
          </a>
        );
      })}
    </div>
  );
}

function ReviewBlockCard({ block, compact = false }) {
  const badgeText = block.badgeText || "";
  const heading = block.heading || "How did we do today?";
  const helperText = block.helperText || "Tap the stars to leave a quick review.";
  const cardBgColor = block.cardBgColor || "#FFF8E7";
  const headingColor = block.headingColor || "#1F2937";
  const helperColor = block.helperColor || "#6B7280";
  const accentColor = block.accentColor || "#F59E0B";
  const badgeTextColor = block.badgeTextColor || accentColor;
  const borderColor = block.borderColor || "#F3D19C";
  const cardRadius = block.cardRadius || "18px";
  const textAlign = block.textAlign || "center";
  const justifyContent = getFlexAlignmentForText(textAlign);
  const hasReviewUrl = Boolean(block.url && block.url.trim());
  const clickThroughMinRating = getReviewClickThroughMinRating(block);
  const surveyLinkUrl = getReviewSurveyUrl(block);
  const hasSurveyUrl = Boolean(surveyLinkUrl.trim());
  const wrapperPadding = compact ? "8px 12px" : "18px 30px";
  const cardPadding = compact ? "14px 14px 16px" : "20px 22px 22px";
  const titleSize = compact ? 18 : 30;
  const helperSize = compact ? 11 : 13;
  const badgeSize = compact ? 10 : 11;
  const starSize = getReviewStarSize(block, compact);
  const cardShadow = getReviewCardShadow(compact);

  return (
    <div style={{ padding: wrapperPadding }}>
      <div
        style={{
          background: cardBgColor,
          border: `1px solid ${borderColor}`,
          borderRadius: cardRadius,
          padding: cardPadding,
          textAlign,
          boxShadow: cardShadow,
        }}
      >
        {badgeText ? (
          <div style={{ marginBottom: compact ? 10 : 12 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: compact ? "4px 8px" : "5px 10px",
                borderRadius: 999,
                background: "#FFFFFF",
                border: `1px solid ${borderColor}`,
                color: badgeTextColor,
                fontSize: badgeSize,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {badgeText}
            </span>
          </div>
        ) : null}
        <div style={{ fontSize: titleSize, lineHeight: 1.2, fontWeight: 700, color: headingColor }}>
          {heading}
        </div>
        <div style={{ display: "flex", justifyContent, gap: compact ? 6 : 8, margin: compact ? "10px 0 8px" : "12px 0 10px" }}>
          {Array.from({ length: REVIEW_STAR_COUNT }, (_, index) => {
            const rating = index + 1;
            const isReviewStar = hasReviewUrl && rating >= clickThroughMinRating;
            const isSurveyStar = hasSurveyUrl && rating < clickThroughMinRating;
            const starStyles = { color: accentColor, fontSize: starSize, lineHeight: 1 };

            if (isReviewStar || isSurveyStar) {
              return (
                <a
                  key={index}
                  href={ensureAbsoluteUrl(isReviewStar ? block.url : surveyLinkUrl)}
                  onClick={(e) => e.preventDefault()}
                  style={{ ...starStyles, textDecoration: "none", cursor: "pointer" }}
                >
                  ★
                </a>
              );
            }

            return (
              <span key={index} style={{ ...starStyles, cursor: "default" }}>
                ★
              </span>
            );
          })}
        </div>
        {helperText ? (
          <div style={{ fontSize: helperSize, lineHeight: 1.6, color: helperColor }}>
            {helperText}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   EMAIL FOOTER (permanent)
   ═══════════════════════════════════════════════════════════════════ */
function EmailFooter({ brandName, design }) {
  const footerDesign = normalizeEmailDesign(design);
  const footerBgColor = footerDesign.footerBgColor || DEFAULT_EMAIL_DESIGN.footerBgColor;
  const footerTextColor = footerDesign.footerTextColor || DEFAULT_EMAIL_DESIGN.footerTextColor;
  const footerLinkColor = footerDesign.footerLinkColor || DEFAULT_EMAIL_DESIGN.footerLinkColor;
  const footerFontSize = getFooterFontSize(footerDesign);
  const footerLineHeight = `${Math.max(18, Math.round(footerFontSize * 1.5))}px`;
  const footerLines = getFooterLines(footerDesign);

  return (
    <div style={{ background: footerBgColor, padding: "28px 30px 24px", textAlign: "center", marginBottom: 0 }}>
      {footerLines.map((line, index) => {
        const baseStyle = {
          color: footerTextColor,
          marginTop: index === 0 ? 0 : 8,
          fontFamily: design?.globalFontFamily || "Arial, sans-serif",
          fontSize: footerFontSize,
          lineHeight: footerLineHeight,
          whiteSpace: "pre-line",
        };

        if (line.type === "browser") {
          return (
            <div key={line.id} style={baseStyle}>
              <a href="https://example.com/email-browser" onClick={(e) => e.preventDefault()} style={{ color: footerLinkColor, textDecoration: "underline" }}>
                {line.linkText || FOOTER_LINE_DEFAULTS.browser.linkText}
              </a>
            </div>
          );
        }

        if (line.type === "preferences") {
          const introText = resolveFooterTemplateText(line.introText, brandName).trim();
          return (
            <React.Fragment key={line.id}>
              {introText ? (
                <div style={baseStyle}>
                  {introText}
                </div>
              ) : null}
              <div style={{ ...baseStyle, marginTop: introText ? 2 : baseStyle.marginTop }}>
                <a href="https://example.com/preferences" onClick={(e) => e.preventDefault()} style={{ color: footerLinkColor, textDecoration: "underline" }}>
                  {line.preferencesText || FOOTER_LINE_DEFAULTS.preferences.preferencesText}
                </a>
                <span>{line.separatorText || FOOTER_LINE_DEFAULTS.preferences.separatorText}</span>
                <a href="https://example.com/unsubscribe" onClick={(e) => e.preventDefault()} style={{ color: footerLinkColor, textDecoration: "underline" }}>
                  {line.unsubscribeText || FOOTER_LINE_DEFAULTS.preferences.unsubscribeText}
                </a>
              </div>
            </React.Fragment>
          );
        }

        const lineText = resolveFooterTemplateText(line.text, brandName).trim();
        if (!lineText) return null;

        return (
          <div key={line.id} style={baseStyle}>
            {lineText}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   SIDEBAR BLOCK CARD
   ═══════════════════════════════════════════════════════════════════ */
function SidebarBlockCard({ type, label, onClick }) {
  const iconMap = {
    image: <ImageIcon width={20} height={20} />,
    heading: <TextIcon width={20} height={20} />,
    paragraph: <TextIcon width={20} height={20} />,
    button: <Link2Icon width={20} height={20} />,
    review: <StarIcon width={20} height={20} />,
    divider: <DividerHorizontalIcon width={20} height={20} />,
    spacer: <WidthIcon width={20} height={20} />,
    social: SOCIAL_ICONS.instagram(20, "currentColor"),
    logo: <ImageIcon width={20} height={20} />,
    columns: <ColumnsIcon width={20} height={20} />,
  };

  return (
    <button
      onClick={onClick}
      style={{
        all: "unset",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "14px 4px",
        borderRadius: "var(--radius-2)",
        border: "1px solid var(--gray-a4)",
        background: "var(--color-background)",
        cursor: "grab",
        transition: "all 0.15s",
        width: "100%",
        textAlign: "center",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--gray-a6)"; e.currentTarget.style.background = "var(--gray-a2)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--gray-a4)"; e.currentTarget.style.background = "var(--color-background)"; }}
    >
      <span style={{ color: "var(--gray-9)" }}>{iconMap[type] || <CodeIcon width={20} height={20} />}</span>
      <span style={{ fontSize: 11.5, fontWeight: 500, color: "var(--gray-9)" }}>{label}</span>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   SHARED UI HELPERS
   ═══════════════════════════════════════════════════════════════════ */
function PropField({ label, children }) {
  return (
    <Flex direction="column" gap="1">
      <Text size="1" weight="medium">{label}</Text>
      {children}
    </Flex>
  );
}

function ColorInput({ value, onChange }) {
  return (
    <Flex align="center" gap="2" style={{ border: "1px solid var(--gray-a5)", borderRadius: "var(--radius-2)", padding: "4px 10px" }}>
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} style={{ width: 24, height: 24, border: "none", borderRadius: 4, cursor: "pointer", padding: 0, background: "none" }} />
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} style={{ flex: 1, border: "none", outline: "none", fontSize: 12, fontFamily: "monospace", background: "transparent", color: "var(--gray-12)" }} />
    </Flex>
  );
}
